/**
 * 管理后台「经营分析」六页文案（韩、中、英）：经营驾驶舱、仪表盘、付费分析、成本测算、成本管控、用量统计。
 *
 * 背景：页面按 Alice 的版式复刻，措辞跟随 Alice，品牌与口径按本仓适配：糖豆换成花瓣（1 美元 = 1000 花瓣）、
 *   人民币换成美元、用户换成账号、内部账号换成仅记账（metered）请求、微信收款换成在线支付（扣除退款）。
 * 设计意图：
 *   - 按页面分组（mu、cc、ov、bc、ca、an），共用文案在 common；指标说明角标的长文在 BIZ_HELP，页面按 key 取。
 *   - 经营驾驶舱与付费分析的结论文案全部在这里按后端给的 key 取，接口里没有中文。
 *   - 后端新增的错误码在 errors 里映射，其余沿用共用错误表（见 biz-kit 的 bizError）。
 * 关键约束：韩文用 해요체；中文不用破折号，引号用「」；占位符统一 {name}，不在文案里写死金额符号以外的数字。
 *   Alice 的 50 元、20 元档位建议换成等值美元档位（7 美元、3 美元），登记在 docs/Alice 对照与复刻计划.md。
 */
import type { Entry } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

export const BIZ = {
  common: {
    to: e('至', '~', 'to'),
    clear: e('清空', '초기화', 'Clear'),
    save: e('保存', '저장', 'Save'),
    cancel: e('取消', '취소', 'Cancel'),
    delete: e('刪除', '삭제', 'Delete'),
    loading: e('載入中…', '불러오는 중…', 'Loading…'),
    noData: e('暫無資料', '데이터가 없어요', 'No data'),
    paymentsDown: e(
      '支付服務暫時取不到，收入、儲值與付費相關數字為 0，不代表沒有收入。',
      '결제 서비스에서 데이터를 가져오지 못했어요. 매출, 충전, 결제 관련 수치는 0으로 표시되며 실제 매출이 없다는 뜻은 아니에요.',
      'The payment service is unreachable. Revenue, recharge and payer figures show 0 and do not mean there is no revenue.',
    ),
  },

  errors: {
    invalid_request: e('參數不合法', '요청 값이 올바르지 않아요', 'Invalid request'),
    invalid_date: e('日期格式不合法，應為 YYYY-MM-DD', '날짜 형식이 올바르지 않아요 (YYYY-MM-DD)', 'Invalid date, expected YYYY-MM-DD'),
    invalid_range: e('日期範圍不合法：起始不能晚於截止，最長 90 天', '날짜 범위가 올바르지 않아요. 시작일이 종료일보다 늦을 수 없고 최대 90일이에요', 'Invalid date range: start must not be after end, 90 days at most'),
    not_found: e('找不到這條記錄', '항목을 찾을 수 없어요', 'Not found'),
    redirect_depends_on_channel: e(
      '改寫目標的路由鏈裡還有走帳渠道，需要先把這些渠道設為「不走帳」',
      '개편 대상의 라우팅 체인에 정산 채널이 남아 있어요. 먼저 해당 채널을 「정산 안 함」으로 바꿔 주세요',
      'The redirect target still routes through billed channels. Set those channels to unbilled first',
    ),
  },

  // ------------------------------------------------------------------ 用量统计
  mu: {
    desc: e('按模型與時間範圍查看每個帳號的 Token 消耗', '모델과 기간별로 계정의 토큰 사용량을 확인해요', 'Token usage per account by model and time range'),
    chip: e('僅統計計費成功記錄', '과금 성공 기록만 집계해요', 'Successful billed records only'),
    totalTokens: e('總 Token', '전체 토큰', 'Total tokens'),
    ioSub: e('輸入 {i} · 輸出 {o}', '입력 {i} · 출력 {o}', 'In {i} · Out {o}'),
    accounts: e('使用帳號數', '사용 계정 수', 'Accounts'),
    perOrg: e('帳號人均 {n} Token', '계정당 {n} 토큰', '{n} tokens per account'),
    requests: e('請求數', '요청 수', 'Requests'),
    perReq: e('平均每次 {n} Token', '요청당 {n} 토큰', '{n} tokens per request'),
    cost: e('計費金額', '과금 금액', 'Billed amount'),
    cacheSub: e('快取命中佔輸入 {p}%', '캐시 적중 {p}% (입력 대비)', 'Cache hit {p}% of input'),
    totalBadge: e('共 {n} 個帳號', '총 {n}개 계정', '{n} accounts'),
    allModels: e('全部模型', '전체 모델', 'All models'),
    start: e('起始日期', '시작일', 'Start date'),
    end: e('截止日期', '종료일', 'End date'),
    today: e('今天', '오늘', 'Today'),
    last7: e('近 7 天', '최근 7일', 'Last 7 days'),
    last30: e('近 30 天', '최근 30일', 'Last 30 days'),
    exclMetered: e('排除僅記帳', '기록만 하는 요청 제외', 'Exclude metered-only'),
    note: e(
      '時間按北京時間自然日（雙側包含），留空為全部歷史。帳號按所選範圍內的總 Token 降序排列，「佔比」為該帳號佔當前篩選結果總 Token 的比例；快取命中是輸入 Token 中命中快取的部分。僅記帳請求不扣花瓣，計費金額只含扣費請求。',
      '시간은 베이징 시간 기준 일 단위(양끝 포함)이고, 비워 두면 전체 기간이에요. 계정은 선택 범위의 총 토큰 내림차순이며, 「점유율」은 현재 결과의 총 토큰 중 해당 계정 비중이에요. 캐시 적중은 입력 토큰 중 캐시에 적중한 부분이에요. 기록만 하는 요청은 꽃잎을 차감하지 않아 과금 금액에는 차감 요청만 포함돼요.',
      'Dates follow Beijing time calendar days (inclusive); leave empty for all history. Accounts are sorted by total tokens in range; share is relative to the filtered total. Cache hits are the part of input tokens served from cache. Metered-only requests deduct no petals, so the billed amount covers charged requests only.',
    ),
    colOrg: e('帳號', '계정', 'Account'),
    colRequests: e('請求數', '요청 수', 'Requests'),
    colInput: e('輸入', '입력', 'Input'),
    colOutput: e('輸出', '출력', 'Output'),
    colCached: e('快取命中', '캐시 적중', 'Cached'),
    colTotal: e('總 Token', '전체 토큰', 'Total tokens'),
    colShare: e('佔比', '점유율', 'Share'),
    colCost: e('計費', '과금', 'Billed'),
    colLast: e('最近使用', '최근 사용', 'Last used'),
    empty: e('所選範圍內沒有用量', '선택한 범위에 사용량이 없어요', 'No usage in range'),
    metered: e('僅記帳', '기록만', 'Metered'),
  },

  // ------------------------------------------------------------------ 成本管控
  cc: {
    desc: e(
      '設定哪些渠道走帳、僅記帳請求的模型改寫與每日上限，以及走帳成本告警閾值',
      '어떤 채널을 정산할지, 기록만 하는 요청의 모델 개편과 일일 한도, 정산 원가 경고 기준을 설정해요',
      'Choose which channels are billed, model redirects and daily caps for metered-only requests, and cost alert thresholds',
    ),
    chip: e('僅記帳請求專用', '기록만 하는 요청 전용', 'For metered-only requests'),
    note: e(
      '走帳的渠道：所有請求（含僅記帳請求）的上遊成本都計入運營看板並參與告警。不走帳的渠道上，僅記帳請求的成本記 0。配置保存後寫入審計日誌。',
      '정산하는 채널: 기록만 하는 요청을 포함한 모든 요청의 업스트림 원가가 운영 대시보드에 반영되고 경고 대상이 돼요. 정산하지 않는 채널에서 기록만 하는 요청의 원가는 0으로 기록해요. 저장하면 감사 로그에 남아요.',
      'Billed channels: upstream cost of every request, metered-only included, counts in dashboards and alerts. On unbilled channels the cost of metered-only requests is recorded as 0. Saves are written to the audit log.',
    ),
    notEnforced: e(
      '改寫目標與每日上限規則已保存並審計，資料面暫未按這兩項攔截或改寫請求；渠道走帳標誌與告警閾值已經生效。',
      '개편 대상과 일일 한도 규칙은 저장되고 감사되지만, 데이터 플레인은 아직 이 두 항목으로 요청을 막거나 바꾸지 않아요. 채널 정산 설정과 경고 기준은 이미 적용돼요.',
      'Redirect targets and daily caps are saved and audited, but the data plane does not yet block or rewrite requests with them. Channel accounting flags and alert thresholds are already in effect.',
    ),
    provTitle: e('渠道走帳設定', '채널 정산 설정', 'Channel accounting'),
    provCopy: e('只有確屬內部結算的渠道才設為「不走帳」', '내부 정산 채널만 「정산 안 함」으로 설정하세요', 'Only channels used for internal settlement should be set to unbilled'),
    colProvider: e('渠道', '채널', 'Channel'),
    colAccounting: e('走帳', '정산', 'Accounting'),
    noProviders: e('還沒有渠道', '채널이 없어요', 'No channels yet'),
    enabled: e('啟用', '사용', 'Enabled'),
    disabled: e('停用', '중지', 'Disabled'),
    billed: e('走帳', '정산', 'Billed'),
    unbilled: e('不走帳', '정산 안 함', 'Unbilled'),
    saved: e('已保存', '저장했어요', 'Saved'),
    redirTitle: e('僅記帳請求的模型改寫', '기록만 하는 요청의 모델 개편', 'Model redirects for metered-only requests'),
    redirCopy: e(
      '僅記帳請求左側模型時，自動改走右側的內部模型。目標必須可路由，且路由鏈全部是不走帳渠道。留空保存即取消改寫',
      '기록만 하는 요청이 왼쪽 모델을 호출하면 오른쪽 내부 모델로 보내요. 대상은 라우팅이 가능해야 하고 체인이 모두 정산하지 않는 채널이어야 해요. 비워서 저장하면 개편이 해제돼요',
      'Metered-only requests for the model on the left are sent to the internal model on the right. The target must be routable and every channel in its chain must be unbilled. Save empty to remove a redirect',
    ),
    redirSearch: e('搜尋模型 ID 或名稱以新增改寫', '모델 ID 또는 이름으로 검색해 개편 추가', 'Search a model ID or name to add a redirect'),
    redirHint: e('未搜尋時只顯示已設定改寫的模型', '검색하지 않으면 개편이 설정된 모델만 보여요', 'Without a search only models with a redirect are shown'),
    colRedirFrom: e('被改寫的模型', '개편되는 모델', 'Redirected model'),
    colRedirTo: e('改寫到（模型 ID）', '개편 대상 (모델 ID)', 'Redirect to (model ID)'),
    noModels: e('沒有匹配的模型', '일치하는 모델이 없어요', 'No matching models'),
    redirPlaceholder: e('留空取消改寫', '비우면 개편 해제', 'Empty to clear'),
    redirSaved: e('改寫已保存', '개편을 저장했어요', 'Redirect saved'),
    redirCleared: e('改寫已取消', '개편을 해제했어요', 'Redirect cleared'),
    capTitle: e('僅記帳請求每日上限', '기록만 하는 요청 일일 한도', 'Daily caps for metered-only requests'),
    capCopy: e(
      '每個帳號每個北京時間自然日、同一類模型的上遊成本上限（美元）。匹配串按小寫子串匹配請求的模型名',
      '계정별로 베이징 시간 하루 기준, 같은 종류 모델의 업스트림 원가 한도(USD)예요. 매칭 문자열은 소문자 부분 문자열로 요청 모델명과 비교해요',
      'Per account, per Beijing calendar day, upstream cost cap (USD) for a class of models. The match string is a lowercase substring of the requested model name',
    ),
    capEmpty: e('還沒有規則', '규칙이 없어요', 'No rules yet'),
    capName: e('規則名稱', '규칙 이름', 'Rule name'),
    capMatch: e('匹配串（小寫子串）', '매칭 문자열 (소문자 부분 문자열)', 'Match (lowercase substring)'),
    capUsd: e('每日上限（美元）', '일일 한도 (USD)', 'Daily cap (USD)'),
    capAdd: e('新增規則', '규칙 추가', 'Add rule'),
    capSave: e('保存上限規則', '한도 규칙 저장', 'Save cap rules'),
    capsSaved: e('上限規則已保存', '한도 규칙을 저장했어요', 'Cap rules saved'),
    thTitle: e('走帳成本告警閾值（美元）', '정산 원가 경고 기준 (USD)', 'Billed-cost alert thresholds (USD)'),
    thCopy: e('只統計僅記帳請求在走帳渠道上的成本，填 0 關閉該檔', '정산 채널에서 기록만 하는 요청의 원가만 집계해요. 0이면 해당 기준을 꺼요', 'Counts only the cost of metered-only requests on billed channels. 0 turns a tier off'),
    account_daily_usd: e('單帳號每日', '계정별 일일', 'Per account daily'),
    total_daily_usd: e('僅記帳合計每日', '기록만 하는 요청 일일 합계', 'Metered total daily'),
    account_hourly_usd: e('單帳號每小時', '계정별 시간당', 'Per account hourly'),
    thSave: e('保存閾值', '기준 저장', 'Save thresholds'),
    thSaved: e('閾值已保存', '기준을 저장했어요', 'Thresholds saved'),
    watchTitle: e('今日走帳成本監控', '오늘 정산 원가 모니터링', 'Billed cost watch today'),
    watchCopy: e('對照上面的閾值，看今天哪些帳號接近或超過', '위 기준과 비교해 오늘 한도에 근접했거나 초과한 계정을 보여요', 'Compares today against the thresholds above'),
    watchTotal: e('今日合計 {c} / 上限 {l}', '오늘 합계 {c} / 한도 {l}', 'Today {c} / limit {l}'),
    watchOff: e('未設定', '미설정', 'off'),
    watchNone: e('今天沒有帳號接近閾值', '오늘 기준에 근접한 계정이 없어요', 'No account is near a threshold today'),
    colOrg: e('帳號', '계정', 'Account'),
    colWindow: e('窗口', '구간', 'Window'),
    colCost: e('已知成本', '확인된 원가', 'Known cost'),
    colLimit: e('閾值', '기준', 'Limit'),
    winDaily: e('每日', '일일', 'Daily'),
    winHourly: e('每小時', '시간당', 'Hourly'),
  },

  // ------------------------------------------------------------------ 仪表盘
  ov: {
    title: e('運營駕駛艙', '운영 대시보드', 'Operations cockpit'),
    desc: e('把帳號增長、商業收入與網關消耗放在同一張經營視圖中', '계정 성장, 상업 매출, 게이트웨이 소비를 한 화면에서 봐요', 'Account growth, commercial revenue and gateway consumption in one view'),
    waiting: e('等待資料', '데이터 대기 중', 'Waiting for data'),
    updated: e('{p}資料 · {s}', '{p} 데이터 · {s}', '{p} data · {s}'),
    scopeExcl: e('已排除僅記帳請求', '기록만 하는 요청 제외', 'metered-only excluded'),
    scopeIncl: e('含僅記帳請求', '기록만 하는 요청 포함', 'metered-only included'),
    scopeTitle: e('排除僅記帳（不扣花瓣）的請求、Token 與消費', '꽃잎을 차감하지 않는 기록만 하는 요청, 토큰, 소비를 제외해요', 'Exclude metered-only requests (no petals deducted), their tokens and spend'),
    excludeInternal: e('排除僅記帳', '기록만 하는 요청 제외', 'Exclude metered-only'),
    presetDays: e('{n}天', '{n}일', '{n}d'),
    periodToday: e('今日', '오늘', 'Today'),
    periodDays: e('近{n}天', '최근 {n}일', 'Last {n} days'),
    badRange: e('開始日期不能晚於結束日期', '시작일은 종료일보다 늦을 수 없어요', 'Start date cannot be after end date'),
    rangeTooLong: e('區間最長 90 天', '기간은 최대 90일이에요', 'Range is 90 days at most'),
    noData: e('無資料', '데이터 없음', 'no data'),
    orgUnit: e('個', '개', ''),
    timesUnit: e('次', '회', ''),
    petalsUnit: e('{n} 花瓣', '꽃잎 {n}', '{n} petals'),
    kActive: e('{p}活躍', '{p} 활성', '{p} active'),
    kTotalOrgs: e('累計帳號', '누적 계정', 'Total accounts'),
    kRequests: e('{p}請求', '{p} 요청', '{p} requests'),
    kPerOrg: e('活躍帳號平均', '활성 계정당', 'Per active account'),
    kCharge: e('{p}使用者消費', '{p} 사용자 소비', '{p} spend'),
    kTotalCharge: e('累計消費', '누적 소비', 'Total spend'),
    kModels: e('已發布模型', '게시된 모델', 'Published models'),
    kModelsSub: e('可運營的模型目錄 · 活躍 Key {n}', '운영 중인 모델 목록 · 활성 키 {n}', 'Operable catalog · active keys {n}'),
    pnlTitle: e('{p}商業盈虧（營收 − 商業成本）', '{p} 상업 손익 (매출 − 상업 원가)', '{p} commercial P&L (revenue − commercial cost)'),
    pnlPill: e('商業口徑', '상업 기준', 'Commercial'),
    pnlCopy: e('營收為在線支付實收（扣除退款），扣除已知上遊成本；僅記帳成本單獨列示', '매출은 온라인 결제 실수령액(환불 차감)이고 확인된 업스트림 원가를 뺀 값이에요. 기록만 하는 원가는 따로 보여줘요', 'Revenue is online payment received net of refunds, minus known upstream cost; metered-only cost is shown separately'),
    pnlRevenue: e('營收', '매출', 'Revenue'),
    pnlCost: e('商業成本', '상업 원가', 'Commercial cost'),
    pnlInternal: e('僅記帳成本', '기록만 하는 원가', 'Metered cost'),
    pnlMetered: e('另有 {n} 次僅記帳請求（刊例價 {v}）不計入營收與商業成本。', '기록만 하는 요청 {n}건(정가 {v})은 매출과 상업 원가에 넣지 않았어요.', '{n} metered-only requests (list {v}) are not counted in revenue or commercial cost.'),
    pnlUnknown: e('{n} 次商業請求成本未知，未計入成本。', '상업 요청 {n}건은 원가를 알 수 없어 원가에 넣지 않았어요.', '{n} commercial requests have unknown cost and are not counted.'),
    rechargeTitle: e('{p}儲值（在線支付實收）', '{p} 충전 (온라인 결제 실수령)', '{p} recharge (online payments received)'),
    rechargeSub: e('{o} 筆訂單 · {p} 個帳號付費', '주문 {o}건 · 결제 계정 {p}개', '{o} orders · {p} paying accounts'),
    payRate: e('付費率', '결제율', 'Pay rate'),
    tokensTitle: e('{p} Token 消耗', '{p} 토큰 사용량', '{p} token usage'),
    tokFull: e('完整', '전체', 'Full'),
    tokCompact: e('緊湊', '간략', 'Compact'),
    tokPerOrg: e('帳號平均', '계정당', 'Per account'),
    chargePerOrg: e('帳號平均消費', '계정당 소비', 'Spend per account'),
    cacheRate: e('快取利用率', '캐시 활용률', 'Cache rate'),
    cacheHit: e('快取命中', '캐시 적중', 'Cache hits'),
    tokIn: e('輸入', '입력', 'Input'),
    tokOut: e('輸出', '출력', 'Output'),
    tokCached: e('快取命中', '캐시 적중', 'Cached'),
    trendTitle: e('經營趨勢', '경영 추이', 'Trends'),
    trendCopy: e('收入、成本、請求和資源消耗的周期變化', '매출, 원가, 요청, 리소스 소비의 기간별 변화', 'Revenue, cost, requests and resource use over time'),
    usd: e('美元', 'USD', 'USD'),
    cPnl: e('營收與成本結構', '매출과 원가 구조', 'Revenue and cost structure'),
    cCharge: e('使用者消費趨勢', '사용자 소비 추이', 'Spend trend'),
    cRequests: e('請求吞吐', '요청 처리량', 'Request throughput'),
    cRequestsSub: e('請求數', '요청 수', 'requests'),
    cTokens: e('Token 結構', '토큰 구조', 'Token mix'),
    cTokensSub: e('輸入 / 輸出 / 快取', '입력 / 출력 / 캐시', 'input / output / cache'),
    cModel: e('成本按模型', '모델별 원가', 'Cost by model'),
    cModelSub: e('美元 · 含僅記帳', 'USD · 기록만 포함', 'USD · incl. metered'),
    cScene: e('成本按場景', '장면별 원가', 'Cost by scene'),
    cSceneSub: e('需要請求帶細分來源', '세부 소스가 필요해요', 'needs a detailed source tag'),
    sceneEmpty: e(
      '本倉的請求沒有細分場景來源，這張圖沒有對應資料。對應 Alice 的「生活線成本按場景」，已登記為沒有對應物。',
      '이 저장소의 요청에는 세부 장면 소스가 없어 이 차트에 해당하는 데이터가 없어요. Alice의 「라이프라인 장면별 원가」에 해당하며 대응물 없음으로 등록했어요.',
      'Requests in this repository carry no detailed scene source, so this chart has no data. It maps to Alice scene-level cost and is registered as having no counterpart.',
    ),
    chartRevenue: e('營收（$）', '매출 ($)', 'Revenue ($)'),
    chartCharge: e('消費（$）', '소비 ($)', 'Spend ($)'),
    chartRequests: e('請求數', '요청 수', 'Requests'),
    chartNew: e('新增', '신규', 'New'),
    unclassified: e('未分類', '미분류', 'Unclassified'),
    otherItems: e('其他 {n} 項', '기타 {n}개', 'Other {n}'),
    tipTotal: e('合計: ${v}', '합계: ${v}', 'Total: ${v}'),
    tipCost: e('成本合計: ${c}（僅記帳 ${i}）', '원가 합계: ${c} (기록만 ${i})', 'Total cost: ${c} (metered ${i})'),
    tipNet: e('商業淨收益: {v}', '상업 순이익: {v}', 'Commercial net: {v}'),
    growthTitle: e('帳號增長與留存', '계정 성장과 유지율', 'Growth and retention'),
    growthCopy: e('觀察活躍、新增與長期使用黏性', '활성, 신규, 장기 사용 정착도를 살펴봐요', 'Active, new and long-term stickiness'),
    cDau: e('活躍與新增帳號', '활성 및 신규 계정', 'Active and new accounts'),
    cRetention: e('多周期留存率', '다기간 유지율', 'Retention by period'),
    ret1: e('次日留存', '1일 유지', 'Day 1'),
    ret3: e('三日留存', '3일 유지', 'Day 3'),
    ret7: e('七日留存', '7일 유지', 'Day 7'),
    ret14: e('14 日留存', '14일 유지', 'Day 14'),
    ret30: e('30 日留存', '30일 유지', 'Day 30'),
    ret60: e('60 日留存', '60일 유지', 'Day 60'),
    topOrgs: e('高價值帳號', '고가치 계정', 'Top accounts'),
    topOrgsSub: e('按消費排序', '소비순', 'by spend'),
    topModels: e('模型消耗排行', '모델 소비 순위', 'Model ranking'),
    topModelsSub: e('費用 / Token / 請求', '비용 / 토큰 / 요청', 'cost / tokens / requests'),
    reqCount: e('{n} 次請求', '요청 {n}건', '{n} requests'),
    modelMeta: e('{t} Token · {r} 次請求 · 上遊 {u}', '{t} 토큰 · 요청 {r}건 · 업스트림 {u}', '{t} tokens · {r} requests · upstream {u}'),
    retDetail: e('留存率明細', '유지율 상세', 'Retention detail'),
    retDetailHint: e('展開查看每日 Cohort 資料', '펼쳐서 일별 코호트 데이터를 확인해요', 'Expand for daily cohort data'),
    colDate: e('日期', '날짜', 'Date'),
  },

  // ------------------------------------------------------------------ 经营驾驶舱
  bc: {
    desc: e('把活躍、餘額、消耗與真實收款轉換成可執行的儲值和贈送決策', '활성, 잔액, 소비, 실제 수금을 실행 가능한 충전/증정 결정으로 바꿔요', 'Turns activity, balance, spend and real payments into recharge and gift decisions'),
    updated: e('{t} 更新', '{t} 업데이트', 'Updated {t}'),
    decisionEyebrow: e('當前建議', '현재 제안', 'Current recommendation'),
    decisionLoading: e('正在計算活動窗口…', '이벤트 시점을 계산하는 중…', 'Calculating the campaign window…'),
    decisionHint: e('系統會綜合付費帳號餘額壓力、真實收入、消費動能、活躍庫存和現有活動狀態。', '결제 계정의 잔액 압박, 실제 매출, 소비 모멘텀, 활성 재고, 기존 이벤트 상태를 종합해요.', 'Combines payer balance pressure, real revenue, spend momentum, active stock and campaign state.'),
    campaignUnavailable: e('當前活動：駕駛艙暫不讀取，請到「儲值活動」頁查看。', '현재 이벤트: 대시보드는 아직 읽지 않아요. 「충전 이벤트」 페이지에서 확인하세요.', 'Current campaign: not read here yet, see the Recharge campaign page.'),
    goCampaign: e('去配置儲值活動', '충전 이벤트 설정', 'Configure campaign'),
    goGift: e('查看贈送兌付', '증정 정산 보기', 'View gift redemption'),
    score: e('儲值窗口指數', '충전 시점 지수', 'Window score'),
    dec: {
      observe: {
        title: e('特殊儲值活動正在進行，先觀察效果', '특별 충전 이벤트가 진행 중이에요. 먼저 효과를 지켜봐요', 'A special campaign is running, observe first'),
        summary: e('避免疊加新活動。關注目標帳號儲值率、贈送面值與後續兌付率。', '새 이벤트를 겹치지 마세요. 대상 계정의 충전율, 증정 금액, 이후 정산율을 확인해요.', 'Avoid stacking a new campaign. Watch recharge rate, gift value and redemption.'),
      },
      broad: {
        title: e('可以開啟全量限時儲值贈送', '전체 대상 기간 한정 충전 증정을 열 수 있어요', 'A broad limited-time recharge gift is viable'),
        summary: e('付費帳號餘額壓力、收入壓力和消費動能同時滿足，適合短周期全量活動。', '결제 계정의 잔액 압박, 매출 압박, 소비 모멘텀이 모두 충족돼 단기 전체 이벤트에 적합해요.', 'Balance pressure, revenue pressure and spend momentum are all met, suited to a short broad campaign.'),
      },
      targeted: {
        title: e('現在適合定向活動，不適合全量撒花瓣', '지금은 타깃 이벤트가 맞고 전체 꽃잎 증정은 맞지 않아요', 'Targeted campaign now, not broad petal giveaways'),
        summary: e('優先覆蓋 {u} 個餘額 7 天內耗盡的付費帳號，以及 {n} 個首充窗口帳號。', '잔액이 7일 안에 바닥나는 결제 계정 {u}곳과 첫 충전 구간 계정 {n}곳을 먼저 공략해요.', 'Prioritize {u} paying accounts running out within 7 days and {n} first-recharge-window accounts.'),
      },
      wait: {
        title: e('暫不需要新增儲值贈送活動', '지금은 새 충전 증정 이벤트가 필요 없어요', 'No new recharge gift campaign needed now'),
        summary: e('目標規模和收入壓力尚未同時達到閾值，繼續觀察並促消耗。', '대상 규모와 매출 압박이 아직 기준을 함께 넘지 않았어요. 계속 지켜보며 소비를 유도해요.', 'Target size and revenue pressure have not reached thresholds together. Keep watching and promote usage.'),
      },
    },
    kActive: e('近 7 天活躍帳號', '최근 7일 활성 계정', 'Active accounts (7d)'),
    kActiveSub: e('{h} 個消費動能高 · {f} 個僅免費活躍', '소비 모멘텀 높음 {h}곳 · 무료만 활성 {f}곳', '{h} high momentum · {f} free-only'),
    kActiveSubDefault: e('真實外部帳號', '실제 외부 계정', 'Real external accounts'),
    kBalance: e('活躍帳號存量', '활성 계정 재고', 'Active account stock'),
    kBalanceSub: e('近 7 天已消耗 {v}', '최근 7일 소비 {v}', 'Spent in 7d: {v}'),
    kBalanceSubDefault: e('當前可用花瓣', '현재 사용 가능한 꽃잎', 'Petals available'),
    kRunway: e('存量覆蓋天數', '재고 커버 일수', 'Stock runway'),
    kRunwaySub: e('活躍帳號庫存 ÷ 近 7 天日均消耗', '활성 계정 재고 ÷ 최근 7일 일평균 소비', 'Active stock ÷ 7-day daily spend'),
    kUrgent: e('立即儲值帳號', '즉시 충전 계정', 'Recharge-now accounts'),
    kUrgentSub: e('{n} 個餘額在 21 天儲值窗口內', '{n}곳이 21일 충전 구간 안', '{n} within the 21-day window'),
    kUrgentSubDefault: e('付費歷史且 7 天內耗盡', '결제 이력이 있고 7일 안에 소진', 'Paid before and out within 7 days'),
    petalsUnit: e('{n} 花瓣', '꽃잎 {n}', '{n} petals'),
    daysUnit: e('{n} 天', '{n}일', '{n} d'),
    noBaseline: e('無上期基線', '비교 기준 없음', 'No baseline'),
    noSpend: e('無消耗', '소비 없음', 'No spend'),
    campaignNA: e('未接入', '미연동', 'Not connected'),
    trigger: {
      balance_pressure: { name: e('付費帳號餘額壓力', '결제 계정 잔액 압박', 'Payer balance pressure'), threshold: e('≤21 天的帳號佔付費活躍消費帳號 ≥35%', '21일 이하 계정이 결제 활성 소비 계정의 35% 이상', 'Accounts at ≤21 days are ≥35% of paying active spenders') },
      revenue_pressure: { name: e('真實收入壓力', '실제 매출 압박', 'Revenue pressure'), threshold: e('近 7 天實收較前 7 天下滑 ≥15%', '최근 7일 실수령이 직전 7일보다 15% 이상 감소', '7-day received revenue down ≥15% vs previous 7 days') },
      spend_momentum: { name: e('消費動能', '소비 모멘텀', 'Spend momentum'), threshold: e('近 7 天花瓣消耗不低於前 7 天的 90%', '최근 7일 꽃잎 소비가 직전 7일의 90% 이상', '7-day petal spend at least 90% of previous 7 days') },
      inventory_pressure: { name: e('活躍庫存壓力', '활성 재고 압박', 'Stock pressure'), threshold: e('活躍帳號總餘額覆蓋天數 ≤60 天', '활성 계정 총 잔액 커버 일수 60일 이하', 'Active stock runway ≤60 days') },
      campaign_exclusive: { name: e('活動互斥', '이벤트 중복 방지', 'Campaign exclusivity'), threshold: e('沒有正在進行的特殊儲值活動（本頁暫未讀取活動，按滿足處理）', '진행 중인 특별 충전 이벤트가 없을 것 (이 페이지는 이벤트를 읽지 않아 충족으로 처리)', 'No special campaign running (not read here, treated as met)') },
      campaign_cooling: { name: e('活動冷卻', '이벤트 쿨다운', 'Campaign cooldown'), threshold: e('上一場特殊活動結束已滿 7 天（本頁暫未讀取活動，按滿足處理）', '지난 특별 이벤트 종료 후 7일 경과 (이 페이지는 이벤트를 읽지 않아 충족으로 처리)', '7 days since the last special campaign (not read here, treated as met)') },
    },
    nextTitle: e('接下來怎麼做', '다음에 할 일', 'What to do next'),
    nextCopy: e('按優先級執行，不必一次做完', '우선순위대로 실행하고 한 번에 다 하지 않아도 돼요', 'Follow the order, no need to finish at once'),
    nextEmpty: e('暫無動作建議', '제안이 없어요', 'No suggestions'),
    next: {
      observe_1: { title: e('只觀察當前活動', '진행 중인 이벤트만 관찰', 'Only observe the current campaign'), detail: e('看目標帳號儲值率、贈送面值和兌付，不要改規則。', '대상 계정의 충전율, 증정 금액, 정산을 보고 규칙은 바꾸지 마세요.', 'Watch recharge rate, gift value and redemption. Do not change the rules.') },
      observe_2: { title: e('核對贈送兌付', '증정 정산 확인', 'Check gift redemption'), detail: e('休眠帳號手裡的未使用贈額不是成本，不要按發完就算花掉。', '휴면 계정이 쓰지 않은 증정분은 원가가 아니에요. 지급했다고 소진된 것으로 보지 마세요.', 'Unused gifts held by dormant accounts are not cost. Do not treat issued as spent.') },
      observe_3: { title: e('準備下一場定向名單', '다음 타깃 명단 준비', 'Prepare the next targeted list'), detail: e('活動結束後，優先觸達付費告急和首充窗口帳號。', '이벤트 종료 후 결제 임박 계정과 첫 충전 구간 계정을 먼저 접촉해요.', 'After it ends, reach urgent payers and first-recharge-window accounts first.') },
      broad_1: { title: e('開 48–72 小時全量限時活動', '48~72시간 전체 한정 이벤트 열기', 'Run a 48–72 hour broad limited event'), detail: e('門檻 7 美元起，額外贈 5%–8%，每帳號一次。', '7달러부터, 5%~8% 추가 증정, 계정당 1회.', 'From $7, extra 5%–8%, once per account.') },
      broad_2: { title: e('排除高庫存帳號', '고재고 계정 제외', 'Exclude high-stock accounts'), detail: e('餘額還能用 60 天以上的帳號不要再加贈。', '잔액이 60일 이상 쓸 수 있는 계정에는 추가 증정을 하지 마세요.', 'Do not top up accounts whose balance lasts over 60 days.') },
      broad_3: { title: e('活動結束後立刻復盤兌付', '종료 직후 정산 복기', 'Review redemption right after'), detail: e('看真實收款、新增付費和未兌付贈額，再決定下一場。', '실제 수금, 신규 결제, 미정산 증정분을 보고 다음 이벤트를 정해요.', 'Check real payments, new payers and unredeemed gifts before the next one.') },
      targeted_1: { title: e('先觸達 {n} 個立即儲值帳號', '즉시 충전 계정 {n}곳 먼저 접촉', 'Reach {n} recharge-now accounts first'), detail: e('48 小時定向儲值贈送，綁定真實付款，不直接發餘額。', '48시간 타깃 충전 증정. 실제 결제와 묶고 잔액을 바로 주지 마세요.', '48-hour targeted recharge gift tied to real payment, no direct balance.') },
      targeted_2: { title: e('給 {n} 個首充窗口帳號設門檻', '첫 충전 구간 계정 {n}곳에 기준 설정', 'Set a threshold for {n} first-recharge accounts'), detail: e('單筆滿 3 美元贈 300 花瓣，單筆滿 7 美元再贈 600 花瓣，共 900，每帳號一次。', '1회 3달러 이상 꽃잎 300, 7달러 이상 추가 600, 합계 900, 계정당 1회.', 'Pay $3 get 300 petals, pay $7 get 600 more, 900 in total, once per account.') },
      targeted_3: { title: e('高庫存帳號只促消耗', '고재고 계정은 소비만 유도', 'High-stock accounts: promote usage only'), detail: e('停止繼續送花瓣，改用量裡程碑或限次模型體驗。', '꽃잎 증정을 멈추고 사용량 마일스톤이나 횟수 제한 모델 체험으로 바꿔요.', 'Stop giving petals. Use usage milestones or limited model trials.') },
      wait_1: { title: e('先促消耗，不新開活動', '먼저 소비 유도, 새 이벤트는 열지 않기', 'Promote usage, no new campaign'), detail: e('把庫存充足的帳號帶到主對話和高價值模型。', '재고가 충분한 계정을 주요 대화와 고가치 모델로 이끌어요.', 'Move well-stocked accounts to the main chat and high-value models.') },
      wait_2: { title: e('只保留小額首充入口', '소액 첫 충전 진입로만 유지', 'Keep only a small first-recharge entry'), detail: e('未付費但已有消耗的帳號，用門檻贈送，不撒通用餘額。', '결제 전이지만 소비가 있는 계정에는 기준형 증정만 하고 일반 잔액은 뿌리지 마세요.', 'For unpaid accounts that already spend, use threshold gifts, not generic balance.') },
      wait_3: { title: e('每天回看駕駛艙', '매일 대시보드 확인', 'Check the cockpit daily'), detail: e('付費告急帳號數上升、收款連續下滑時，再考慮定向活動。', '결제 임박 계정이 늘고 수금이 계속 줄면 그때 타깃 이벤트를 고려해요.', 'Consider a targeted campaign when urgent payers rise and payments keep falling.') },
    },
    dontTitle: e('現在不要做', '지금 하지 말 것', 'Do not do now'),
    dontCopy: e('這些動作會把庫存越堆越高，或把補貼花在不會付費的帳號身上', '이 행동은 재고를 더 쌓거나 결제하지 않을 계정에 보조금을 쓰게 만들어요', 'These actions pile up stock or spend subsidy on accounts that will not pay'),
    dont: {
      no_second_campaign: { title: e('不要疊開第二場特殊儲值活動', '두 번째 특별 충전 이벤트를 겹쳐 열지 마세요', 'Do not stack a second special campaign'), detail: e('先看當前活動的儲值帳號數、贈送面值和後續兌付率，避免兩場補貼互相搶帳號。', '현재 이벤트의 충전 계정 수, 증정 금액, 이후 정산율을 먼저 보고 두 보조금이 서로 계정을 빼앗지 않게 해요.', 'Review the current campaign first so two subsidies do not compete for the same accounts.') },
      no_cooling_broad: { title: e('不要在冷卻期內再開全量活動', '쿨다운 중에 전체 이벤트를 다시 열지 마세요', 'No broad campaign during cooldown'), detail: e('上一場特殊活動剛結束，全量加贈會把還沒消化的庫存繼續堆高。', '직전 특별 이벤트가 막 끝나 전체 추가 증정은 소화되지 않은 재고를 더 쌓아요.', 'The last special campaign just ended; broad top-ups pile on unabsorbed stock.') },
      no_gift_stocked_unpaid: { title: e('不要再給高庫存未付費帳號發通用花瓣', '재고 높은 미결제 계정에 일반 꽃잎을 더 주지 마세요', 'No generic petals for high-stock unpaid accounts'), detail: e('已有 {n} 個帳號餘額能用 60 天以上，繼續送只會推遲首次付費。', '이미 {n}곳이 잔액으로 60일 이상 쓸 수 있어요. 더 주면 첫 결제만 늦춰져요.', '{n} accounts already have over 60 days of balance; more gifts only delay first payment.') },
      no_broad_bonus_stocked_paid: { title: e('不要對庫存充足的付費帳號做全量加贈', '재고 충분한 결제 계정에 전체 추가 증정을 하지 마세요', 'No broad top-up for well-stocked payers'), detail: e('他們缺的是消耗場景，餘額並不缺。優先推高價值功能和限時模型。', '이들에게 부족한 것은 소비 장면이지 잔액이 아니에요. 고가치 기능과 기간 한정 모델을 먼저 밀어요.', 'They lack usage scenarios, not balance. Push high-value features and limited-time models.') },
      no_incentive_free_only: { title: e('不要用餘額刺激只用免費功能的帳號', '무료 기능만 쓰는 계정을 잔액으로 자극하지 마세요', 'Do not incentivize free-only accounts with balance'), detail: e('免費呼叫證明不了付費需求。先把他們帶到主對話。', '무료 호출만으로는 결제 수요를 알 수 없어요. 먼저 주요 대화로 이끌어요.', 'Free calls do not prove paying demand. Bring them to the main chat first.') },
      no_anxious_broad: { title: e('不要因為焦慮開全量儲值贈送', '불안하다고 전체 충전 증정을 열지 마세요', 'Do not open a broad gift out of anxiety'), detail: e('目標人群和收入壓力還沒同時達到閾值。現在更適合觀察與促消耗。', '대상과 매출 압박이 아직 함께 기준에 닿지 않았어요. 지금은 관찰과 소비 유도가 맞아요.', 'Target and revenue pressure have not reached thresholds together. Observe and promote usage.') },
      no_big_gift: { title: e('不要直接大額撒花瓣', '큰 금액의 꽃잎을 바로 뿌리지 마세요', 'Do not hand out large petal gifts directly'), detail: e('預設綁定現金儲值；小額、短周期、限一次，比一次發很多更安全。', '기본은 실제 충전과 묶어요. 소액, 단기, 1회 한정이 한 번에 많이 주는 것보다 안전해요.', 'Tie gifts to real recharge by default; small, short and once is safer than one large grant.') },
    },
    invTitle: e('活躍帳號存量結構', '활성 계정 재고 구조', 'Active stock structure'),
    invCopy: e('先看付費帳號還剩多少天，再決定開不開儲值贈送', '결제 계정의 남은 일수를 먼저 보고 충전 증정 여부를 정해요', 'See how many days payers have left before deciding on a gift'),
    bucket: {
      lt3: e('3 天內', '3일 이내', 'Under 3 days'),
      d3_7: e('3–7 天', '3~7일', '3–7 days'),
      d8_21: e('8–21 天', '8~21일', '8–21 days'),
      d22_60: e('22–60 天', '22~60일', '22–60 days'),
      gt60: e('60 天以上', '60일 이상', 'Over 60 days'),
      no_spend: e('近 7 天無消耗', '최근 7일 소비 없음', 'No spend in 7d'),
    },
    people: e('{n} 個帳號', '{n}곳', '{n} accounts'),
    colRunway: e('可用天數', '사용 가능 일수', 'Runway'),
    colOrgs: e('帳號', '계정', 'Accounts'),
    colPaid: e('其中已付費', '그중 결제함', 'Paid'),
    colUnpaid: e('未付費', '미결제', 'Unpaid'),
    colStock: e('當前存量', '현재 재고', 'Stock'),
    invPaid: e('已付費活躍帳號', '결제한 활성 계정', 'Paying active accounts'),
    invUnpaid: e('未付費活躍帳號', '미결제 활성 계정', 'Unpaid active accounts'),
    invSub: e('存量 {s} · 近 7 天消耗 {v}', '재고 {s} · 최근 7일 소비 {v}', 'Stock {s} · 7d spend {v}'),
    cRevenue: e('真實收款與花瓣消耗', '실제 수금과 꽃잎 소비', 'Real payments and petal spend'),
    cRevenueSub: e('近 14 天 · 收款只含在線支付（扣除退款）', '최근 14일 · 수금은 온라인 결제만(환불 차감)', '14 days · online payments only, net of refunds'),
    cActive: e('活躍帳號趨勢', '활성 계정 추이', 'Active account trend'),
    cActiveSub: e('北京時間自然日', '베이징 시간 기준 일', 'Beijing calendar days'),
    chartRevenue: e('真實收款（$）', '실제 수금 ($)', 'Real payments ($)'),
    chartSpend: e('花瓣消耗（千）', '꽃잎 소비 (천)', 'Petal spend (K)'),
    chartActive: e('活躍帳號', '활성 계정', 'Active accounts'),
    chartPayers: e('付費帳號', '결제 계정', 'Paying accounts'),
    segTitle: e('活躍帳號經營分層', '활성 계정 경영 세그먼트', 'Active account segments'),
    segCopy: e('先區分「該促儲值、該促消耗、該首充轉化」，再決定是否贈送', '「충전 유도, 소비 유도, 첫 충전 전환」을 구분한 뒤 증정 여부를 정해요', 'Separate who to push to recharge, to spend, or to first recharge before gifting'),
    colSegment: e('分層', '세그먼트', 'Segment'),
    colSpend7d: e('近 7 天消耗', '최근 7일 소비', '7d spend'),
    colMedianRunway: e('中位可用天數', '중앙값 일수', 'Median runway'),
    colMeaning: e('判斷', '판단', 'Meaning'),
    colAction: e('建議動作', '권장 행동', 'Suggested action'),
    seg: {
      paid_urgent: { label: e('付費帳號·立即儲值', '결제 계정 · 즉시 충전', 'Paid · recharge now'), meaning: e('近 7 天活躍且有付費歷史，餘額預計 7 天內耗盡', '최근 7일 활성이고 결제 이력이 있으며 잔액이 7일 안에 소진 예상', 'Active in 7d, paid before, balance out within 7 days'), action: e('定向 48 小時儲值贈送', '48시간 타깃 충전 증정', 'Targeted 48-hour recharge gift') },
      paid_soon: { label: e('付費帳號·即將儲值', '결제 계정 · 곧 충전', 'Paid · recharge soon'), meaning: e('餘額預計可用 8–21 天，已進入儲值提醒窗口', '잔액이 8~21일 쓸 수 있어 충전 알림 구간에 들어왔어요', 'Balance lasts 8–21 days, in the reminder window'), action: e('先提醒餘額，必要時小額加贈', '잔액 알림 먼저, 필요하면 소액 추가 증정', 'Remind first, small top-up if needed') },
      trial_ready: { label: e('未付費·首充窗口', '미결제 · 첫 충전 구간', 'Unpaid · first-recharge window'), meaning: e('近期有真實扣費消耗，但從未儲值，餘額不超過 14 天', '최근 실제 차감 소비가 있지만 충전한 적이 없고 잔액이 14일 이내', 'Real spend recently, never recharged, balance within 14 days'), action: e('首充門檻贈送，不直接撒花瓣', '첫 충전 기준형 증정, 꽃잎을 바로 뿌리지 않기', 'Threshold gift for first recharge, no direct petals') },
      paid_healthy: { label: e('付費帳號·餘額健康', '결제 계정 · 잔액 양호', 'Paid · healthy'), meaning: e('有付費歷史，餘額預計可用 22–60 天', '결제 이력이 있고 잔액이 22~60일 쓸 수 있어요', 'Paid before, balance lasts 22–60 days'), action: e('保持日常觸達，不追加補貼', '평소 접촉을 유지하고 보조금은 더하지 않기', 'Keep routine contact, no extra subsidy') },
      trial_nurture: { label: e('未付費·繼續培育', '미결제 · 계속 육성', 'Unpaid · nurture'), meaning: e('從未儲值，仍有 15–60 天可消費餘額', '충전한 적이 없고 15~60일 쓸 수 있는 잔액이 있어요', 'Never recharged, 15–60 days of balance left'), action: e('促使用與價值感知，暫不贈餘額', '사용과 가치 인식을 유도하고 잔액 증정은 보류', 'Drive usage and value, no balance gift yet') },
      paid_stocked: { label: e('付費帳號·庫存充足', '결제 계정 · 재고 충분', 'Paid · well-stocked'), meaning: e('有付費歷史，但現有餘額預計可使用 60 天以上', '결제 이력이 있지만 잔액이 60일 이상 쓸 수 있어요', 'Paid before but balance lasts over 60 days'), action: e('不要促充，優先促消耗', '충전을 권하지 말고 소비를 먼저 유도', 'Do not push recharge, push usage') },
      grant_stocked: { label: e('未付費·贈送庫存高', '미결제 · 증정 재고 높음', 'Unpaid · high gift stock'), meaning: e('從未儲值且餘額預計可使用 60 天以上', '충전한 적이 없고 잔액이 60일 이상 쓸 수 있어요', 'Never recharged and balance lasts over 60 days'), action: e('停止繼續送花瓣，改用功能任務', '꽃잎 증정을 멈추고 기능 과제로 전환', 'Stop gifting petals, use feature tasks') },
      free_only: { label: e('僅免費功能活躍', '무료 기능만 활성', 'Free features only'), meaning: e('近 7 天有呼叫，但沒有產生扣費消耗', '최근 7일 호출은 있지만 차감 소비는 없어요', 'Calls in 7d but no charged spend'), action: e('做產品轉化，不用餘額刺激', '제품 전환을 하고 잔액으로 자극하지 않기', 'Convert through product, not balance') },
    },
    giftTitle: e('怎麼贈送更有效', '어떻게 증정해야 효과적일까요', 'How to gift effectively'),
    giftCopy: e('贈送只是手段；預設優先綁定現金儲值，不給高庫存帳號繼續堆餘額', '증정은 수단일 뿐이에요. 기본은 실제 충전과 묶고 재고 높은 계정에는 잔액을 더 쌓지 않아요', 'Gifting is a means; tie it to real recharge and never pile balance on high-stock accounts'),
    colon: e('：', ': ', ': '),
    gift: {
      paid_urgent: { strategy: e('儲值後贈，不直接發餘額', '충전 후 증정, 잔액 바로 지급 안 함', 'Gift after recharge, no direct balance'), offer: e('48 小時定向活動；7 美元起，額外贈 5%–8%，每帳號一次', '48시간 타깃 이벤트, 7달러부터 5%~8% 추가 증정, 계정당 1회', '48-hour targeted event; from $7, extra 5%–8%, once per account'), reason: e('他們已經持續消費且餘額將盡，贈送應綁定真實收款。', '이미 꾸준히 쓰고 잔액이 곧 바닥나므로 증정은 실제 수금과 묶어야 해요.', 'They spend steadily and will run out soon, so gifts should be tied to real payment.') },
      trial_ready: { strategy: e('首充固定贈送', '첫 충전 고정 증정', 'Fixed first-recharge gift'), offer: e('單筆滿 3 美元贈 300 花瓣，單筆滿 7 美元再贈 600，共 900，每帳號一次', '1회 3달러 이상 꽃잎 300, 7달러 이상 추가 600, 합계 900, 계정당 1회', 'Pay $3 get 300 petals, pay $7 get 600 more, 900 in total, once per account'), reason: e('先讓現金儲值發生，再用小額贈送降低首次決策門檻。', '먼저 실제 충전이 일어나게 하고 소액 증정으로 첫 결정의 문턱을 낮춰요.', 'Make a real payment happen first, then lower the first-decision barrier with a small gift.') },
      grant_stocked: { strategy: e('停止餘額贈送', '잔액 증정 중지', 'Stop balance gifts'), offer: e('改為用量裡程碑、限次模型體驗或功能任務獎勵', '사용량 마일스톤, 횟수 제한 모델 체험, 기능 과제 보상으로 전환', 'Use usage milestones, limited model trials or feature task rewards'), reason: e('存量餘額已經覆蓋很長時間，繼續送只會推遲付費。', '잔액이 이미 오래 쓸 만큼 있어 더 주면 결제만 늦춰져요.', 'Existing balance already lasts long; more gifts only delay payment.') },
      free_only: { strategy: e('先做產品轉化', '먼저 제품 전환', 'Convert through product first'), offer: e('引導進入主對話和高價值功能，不追加通用花瓣', '주요 대화와 고가치 기능으로 이끌고 일반 꽃잎은 더하지 않아요', 'Guide to the main chat and high-value features, no generic petals'), reason: e('只有免費呼叫，送更多餘額不能證明會形成付費需求。', '무료 호출뿐이라 잔액을 더 줘도 결제 수요가 생긴다는 보장이 없어요.', 'Only free calls; more balance does not prove paying demand.') },
    },
    targetTitle: e('本輪優先觸達帳號', '이번 우선 접촉 계정', 'Priority accounts this round'),
    targetCopy: e('只列近 7 天活躍的付費告急、即將儲值與首充窗口帳號，不含電子信箱等敏感資訊', '최근 7일 활성인 결제 임박, 곧 충전, 첫 충전 구간 계정만 보여요. 이메일 등 민감 정보는 없어요', 'Only active payers urgent or soon to recharge and first-recharge accounts; no emails or sensitive data'),
    targetCount: e('共 {n} 個優先帳號', '우선 계정 총 {n}곳', '{n} priority accounts'),
    targetEmpty: e('當前沒有需要優先觸達的帳號', '우선 접촉할 계정이 없어요', 'No priority accounts right now'),
    colOrg: e('帳號', '계정', 'Account'),
    colMomentum: e('消費動能', '소비 모멘텀', 'Momentum'),
    colActiveDays: e('活躍天數', '활성 일수', 'Active days'),
    colBalance: e('餘額', '잔액', 'Balance'),
    colRunwayEst: e('預計可用', '예상 사용 일수', 'Est. runway'),
    colPaidHistory: e('付費歷史', '결제 이력', 'Paid history'),
    prev: e('前 7 天 {v}', '직전 7일 {v}', 'prev 7d {v}'),
    paidHistory: e('{n} 單 · {v}', '{n}건 · {v}', '{n} orders · {v}'),
    momentum: { high: e('高', '높음', 'High'), medium: e('中', '보통', 'Medium'), low: e('低', '낮음', 'Low') },
    methodWindow: e(
      '近 7 天真實收款 {r}（環比 {rg}），花瓣消耗 {s}（環比 {sg}）。',
      '최근 7일 실제 수금 {r} (전기 대비 {rg}), 꽃잎 소비 {s} (전기 대비 {sg}).',
      'Last 7 days real payments {r} (vs previous {rg}), petal spend {s} (vs previous {sg}).',
    ),
    methodNote: e(
      '「消費動能」是根據活躍天數、近 7 天消耗與環比推導的行為代理，不代表帳號的主觀意願。本頁只給建議，不會自動開啟活動或發放花瓣。',
      '「소비 모멘텀」은 활성 일수, 최근 7일 소비, 전기 대비 변화로 추정한 행동 지표이고 계정의 의사를 뜻하지 않아요. 이 페이지는 제안만 하며 이벤트를 열거나 꽃잎을 지급하지 않아요.',
      'Spend momentum is a behavioral proxy derived from active days, 7-day spend and trend, not stated intent. This page only recommends and never starts campaigns or grants petals.',
    ),
  },

  // ------------------------------------------------------------------ 成本测算
  ca: {
    desc: e('自動核算真實上遊成本、覆蓋率、毛利與未知成本風險', '실제 업스트림 원가, 커버리지, 총이익, 원가 미확인 위험을 자동으로 계산해요', 'Upstream cost, coverage, margin and unknown-cost risk, computed automatically'),
    computing: e('計算中…', '계산 중…', 'Computing…'),
    failed: e('載入失敗', '불러오기 실패', 'Load failed'),
    noteDefault: e(
      '上遊成本為空不等於零成本。本頁只把有採購成本記錄的請求計入金額，並把未知請求、Token 與使用者實扣單獨列出。',
      '업스트림 원가가 비어 있다고 원가가 0이라는 뜻은 아니에요. 이 페이지는 매입 원가 기록이 있는 요청만 금액에 넣고 미확인 요청, 토큰, 실차감은 따로 보여줘요.',
      'Empty upstream cost does not mean zero cost. Only requests with a recorded purchase cost count toward amounts; unknown requests, tokens and charges are listed separately.',
    ),
    noteGoodHead: e('成本覆蓋良好（{p}）', '원가 커버리지 양호 ({p})', 'Cost coverage is good ({p})'),
    noteGoodTail: e('：已知成本可直接用於經營判斷；仍有 {n} 次請求未覆蓋。', ': 확인된 원가를 경영 판단에 바로 쓸 수 있어요. 아직 {n}건은 포함되지 않았어요.', ': known cost can be used for decisions; {n} requests are still uncovered.'),
    noteBadHead: e('當前金額只是成本下限，不能當完整成本。', '현재 금액은 원가의 하한일 뿐 전체 원가가 아니에요.', 'Current amounts are a cost floor, not the full cost.'),
    noteBadTail: e(
      '覆蓋率 {p}；還有 {n} 次請求、{tok} Token 的採購成本未知，對應使用者消耗 {c}。未知不等於零成本，需要補齊實際渠道與模型的採購價。',
      '커버리지 {p}. 요청 {n}건, 토큰 {tok}의 매입 원가를 알 수 없고 해당 사용자 소비는 {c}예요. 미확인은 원가 0이 아니므로 실제 채널과 모델의 매입 단가를 채워야 해요.',
      'Coverage {p}; {n} requests and {tok} tokens have unknown purchase cost, with {c} of user spend. Unknown is not zero; fill in the real channel and model purchase prices.',
    ),
    last3: e('近 3 天', '최근 3일', 'Last 3 days'),
    last7: e('近 7 天', '최근 7일', 'Last 7 days'),
    last30: e('近 30 天', '최근 30일', 'Last 30 days'),
    query: e('查詢', '조회', 'Query'),
    badRange: e('起始日期不能晚於截止日期', '시작일은 종료일보다 늦을 수 없어요', 'Start date cannot be after end date'),
    toolbarHint: e('北京時間 · 金額美元 ·「今日經營結果」不隨日期變化', '베이징 시간 · 금액 USD · 「오늘 경영 결과」는 날짜에 따라 바뀌지 않아요', 'Beijing time · USD · today result ignores the date range'),
    tabsLabel: e('成本測算視圖切換', '원가 산정 보기 전환', 'Cost analytics views'),
    tab: { overview: e('經營總覽', '경영 개요', 'Overview'), users: e('帳號成本', '계정 원가', 'Account cost'), models: e('模型與業務', '모델과 업무', 'Models and sources') },
    todayTitle: e('今日經營結果', '오늘 경영 결과', "Today's result"),
    todayCopy: e('北京時間今日截至當前 · 不受日期篩選影響 · 已扣除免費業務補貼', '베이징 시간 오늘 현재까지 · 날짜 필터 영향 없음 · 무료 업무 보조금 차감 후', 'Beijing time today so far · ignores the date filter · after free-business subsidy'),
    todayAsOf: e('{d} 截至當前', '{d} 현재까지', '{d} so far'),
    verdictLoading: e('正在判斷今天是否賺錢…', '오늘 수익이 나는지 판단하는 중…', 'Working out whether today is profitable…'),
    verdictNone: e('今天暫時沒有外部成功帳單，尚無法判斷經營結果。', '오늘은 아직 성공한 외부 과금이 없어 경영 결과를 판단할 수 없어요.', 'No successful external billing yet today, so the result cannot be judged.'),
    profit: e('盈利', '이익', 'profit'),
    loss: e('虧損', '손실', 'loss'),
    verdictOkHead: e('今天截至目前{dir} {v}。', '오늘 현재까지 {dir} {v}.', 'Today so far: {dir} {v}.'),
    verdictOkTail: e('已包含免費業務補貼 {s}；使用者對話已知毛利 {c}。這是花瓣消費口徑，不是今日在線支付到帳。', '무료 업무 보조금 {s}를 포함했고 사용자 대화 확인 총이익은 {c}예요. 꽃잎 소비 기준이며 오늘 온라인 결제 입금액이 아니에요.', 'Includes free-business subsidy {s}; conversation known margin {c}. This is petal consumption, not today online payments received.'),
    verdictUncHead: e('已知口徑目前{dir} {v}，但還不能下完整結論。', '확인된 기준으로 현재 {dir} {v}이지만 아직 결론을 내리기 어려워요.', 'Known basis shows {dir} {v}, but no full conclusion yet.'),
    verdictUncTail: e('成本覆蓋率僅 {p}，仍有 {n} 次請求成本未知；已知金額已扣除免費業務補貼 {s}。', '원가 커버리지가 {p}뿐이고 {n}건은 원가를 알 수 없어요. 확인된 금액은 무료 업무 보조금 {s}를 이미 뺐어요.', 'Cost coverage is only {p} and {n} requests have unknown cost; known amounts already deduct free-business subsidy {s}.'),
    kTodayMargin: e('今日綜合已知毛利', '오늘 종합 확인 총이익', 'Overall known margin today'),
    kTodayMarginSub: e('綜合已知毛利率 {r} · 覆蓋率 {c}', '종합 확인 총이익률 {r} · 커버리지 {c}', 'Overall known margin {r} · coverage {c}'),
    kTodayMarginSubNA: e('覆蓋率 {c} · 毛利率不適用', '커버리지 {c} · 총이익률 해당 없음', 'Coverage {c} · margin rate n/a'),
    kChatRate: e('使用者對話毛利率', '사용자 대화 총이익률', 'Conversation margin rate'),
    kChatRateSub: e('使用者消耗 {c} · 成本覆蓋 {p}', '사용자 소비 {c} · 원가 커버리지 {p}', 'User spend {c} · cost coverage {p}'),
    kChatMargin: e('使用者對話已知毛利', '사용자 대화 확인 총이익', 'Conversation known margin'),
    kChatMarginSub: e('已知成本 {c} · {n} 次請求', '확인된 원가 {c} · 요청 {n}건', 'Known cost {c} · {n} requests'),
    kSubsidy: e('免費業務補貼', '무료 업무 보조금', 'Free-business subsidy'),
    kSubsidySub: e('{n} 次補貼請求 · 使用者消耗 {c}', '보조 요청 {n}건 · 사용자 소비 {c}', '{n} subsidized requests · user spend {c}'),
    biz: {
      conversation: e('使用者對話（可扣費）', '사용자 대화 (과금 가능)', 'User conversation (billable)'),
      moments_subsidy: e('免費業務（補貼）', '무료 업무 (보조)', 'Free business (subsidy)'),
      other: e('其他業務', '기타 업무', 'Other'),
      unattributed: e('日誌未歸因', '로그 미귀속', 'Unattributed'),
      overall: e('全部外部請求', '전체 외부 요청', 'All external'),
    },
    colBiz: e('業務', '업무', 'Business'),
    colKnownCharge: e('已知樣本使用者消耗', '확인 샘플 사용자 소비', 'Known-sample user spend'),
    colKnownCost: e('已知成本', '확인된 원가', 'Known cost'),
    colMargin: e('已知毛利', '확인 총이익', 'Known margin'),
    colMarginRate: e('毛利率', '총이익률', 'Margin rate'),
    colCoverage: e('覆蓋率', '커버리지', 'Coverage'),
    kCompany: e('全站已知成本', '전체 확인 원가', 'Company known cost'),
    kCompanySub: e('外部請求 + 僅記帳', '외부 요청 + 기록만', 'External + metered'),
    kExternal: e('外部請求已知成本', '외부 요청 확인 원가', 'External known cost'),
    kExternalSub: e('{u} 個帳號 · {r} 次請求', '계정 {u}곳 · 요청 {r}건', '{u} accounts · {r} requests'),
    kInternal: e('僅記帳已知成本', '기록만 하는 요청 확인 원가', 'Metered known cost'),
    kInternalSub: e('覆蓋率 {p} · {r} 次請求', '커버리지 {p} · 요청 {r}건', 'Coverage {p} · {r} requests'),
    kCoverage: e('外部成本覆蓋率', '외부 원가 커버리지', 'External cost coverage'),
    kCoverageSub: e('{n} 次成本未知', '{n}건 원가 미확인', '{n} unknown-cost requests'),
    kCharge: e('使用者消耗面值', '사용자 소비 액면가', 'User spend (face value)'),
    kChargeSub: e('花瓣扣費折算，不是現金收入', '꽃잎 차감 환산이며 현금 수입이 아니에요', 'Converted from petals deducted, not cash revenue'),
    kMargin: e('已知樣本毛利', '확인 샘플 총이익', 'Known-sample margin'),
    kMarginSub: e('已知樣本毛利率 {r}', '확인 샘플 총이익률 {r}', 'Known-sample margin rate {r}'),
    kMarginNA: e('成本已知但使用者實扣為 0，毛利率不適用', '원가는 알지만 실차감이 0이라 총이익률은 해당 없어요', 'Cost known but charge is 0, margin rate n/a'),
    kFree: e('免費功能已知成本', '무료 기능 확인 원가', 'Free-feature known cost'),
    kFreeSub: e('{n} 次免費請求', '무료 요청 {n}건', '{n} free requests'),
    kPerOrg: e('帳號平均已知成本', '계정당 확인 원가', 'Known cost per account'),
    kPerOrgSub: e('每千次請求 {c}', '요청 1,000건당 {c}', 'Per 1,000 requests {c}'),
    cCost: e('免費業務補貼與每日毛利', '무료 업무 보조금과 일일 총이익', 'Free subsidy and daily margin'),
    cCostSub: e('美元 · 補貼以負項展示', 'USD · 보조금은 음수로 표시', 'USD · subsidy shown negative'),
    cCoverage: e('每日成本覆蓋率', '일일 원가 커버리지', 'Daily cost coverage'),
    cCoverageSub: e('低於 95% 的日期不可直接當完整成本', '95% 미만인 날은 전체 원가로 쓸 수 없어요', 'Days below 95% are not full cost'),
    chartChat: e('使用者對話已知毛利', '사용자 대화 확인 총이익', 'Conversation known margin'),
    chartSubsidy: e('免費業務補貼（負項）', '무료 업무 보조금 (음수)', 'Free subsidy (negative)'),
    chartOverall: e('綜合已知毛利', '종합 확인 총이익', 'Overall known margin'),
    chartCoverage: e('外部成本覆蓋率', '외부 원가 커버리지', 'External cost coverage'),
    chartFull: e('完整線 95%', '완전 기준선 95%', 'Full line 95%'),
    dailyTitle: e('每日經營明細', '일별 경영 상세', 'Daily detail'),
    dailyCopy: e('點選表頭排序，可匯出 CSV', '헤더를 눌러 정렬하고 CSV로 내보낼 수 있어요', 'Click headers to sort; export as CSV'),
    exportCsv: e('匯出 CSV', 'CSV 내보내기', 'Export CSV'),
    nothingToExport: e('當前沒有可匯出的資料', '내보낼 데이터가 없어요', 'Nothing to export'),
    colDate: e('日期', '날짜', 'Date'),
    colActiveOrgs: e('活躍帳號', '활성 계정', 'Active accounts'),
    colRequests: e('請求', '요청', 'Requests'),
    colCharge: e('使用者消耗', '사용자 소비', 'User spend'),
    colOverallMargin: e('綜合已知毛利', '종합 확인 총이익', 'Overall margin'),
    colOverallRate: e('綜合毛利率', '종합 총이익률', 'Overall rate'),
    colChatMargin: e('使用者對話毛利', '사용자 대화 총이익', 'Conversation margin'),
    colChatRate: e('對話毛利率', '대화 총이익률', 'Conversation rate'),
    colSubsidy: e('免費業務補貼', '무료 업무 보조금', 'Free subsidy'),
    colAttribution: e('業務歸因率', '업무 귀속률', 'Attribution rate'),
    colInternalCost: e('僅記帳成本', '기록만 하는 원가', 'Metered cost'),
    colUnknownReq: e('未知請求', '미확인 요청', 'Unknown requests'),
    colUnknownTokens: e('未知 Token', '미확인 토큰', 'Unknown tokens'),
    usersTitle: e('帳號成本 TOP 100', '계정 원가 TOP 100', 'Account cost TOP 100'),
    usersCopy: e('點選表頭排序 · 收款是在線支付實收，使用者消耗是花瓣面值', '헤더를 눌러 정렬 · 수금은 온라인 결제 실수령, 사용자 소비는 꽃잎 액면가', 'Click headers to sort · payments are online receipts, user spend is petal face value'),
    unknownTitle: e('成本未知風險 TOP 50', '원가 미확인 위험 TOP 50', 'Unknown-cost risk TOP 50'),
    unknownCopy: e('按未知 Token 排序；這裡列的是當前無法準確測算的成本敞口，不等於零成本', '미확인 토큰순이에요. 현재 정확히 산정할 수 없는 원가 노출이며 원가 0이 아니에요', 'Sorted by unknown tokens; this is cost exposure that cannot be measured yet, not zero cost'),
    colOrg: e('帳號', '계정', 'Account'),
    colActiveDays: e('活躍天數', '활성 일수', 'Active days'),
    colTokens: e('Token', '토큰', 'Tokens'),
    colPeriodPaid: e('本期收款', '이번 기간 수금', 'Paid in period'),
    colLifetimePaid: e('累計收款', '누적 수금', 'Lifetime paid'),
    colFreeCost: e('免費成本', '무료 원가', 'Free cost'),
    colUnknownCharge: e('對應使用者消耗', '해당 사용자 소비', 'Matching user spend'),
    colTotalCoverage: e('總覆蓋率', '전체 커버리지', 'Total coverage'),
    modelsTitle: e('模型 / 渠道盈利測算', '모델 / 채널 수익 산정', 'Model and channel margin'),
    modelsCopy: e('按實際路由的模型與渠道分組，比較使用者消耗、渠道成本和已知樣本毛利 · 渠道已知成本降序', '실제 라우팅된 모델과 채널별로 사용자 소비, 채널 원가, 확인 샘플 총이익을 비교해요 · 채널 확인 원가 내림차순', 'Grouped by actually routed model and channel; compares user spend, channel cost and known-sample margin · by known cost'),
    colModel: e('模型', '모델', 'Model'),
    colProvider: e('渠道', '채널', 'Channel'),
    colOrgs: e('帳號', '계정', 'Accounts'),
    colModelCharge: e('使用者總消耗', '사용자 총 소비', 'Total user spend'),
    colComparableCharge: e('可比使用者消耗', '비교 가능 사용자 소비', 'Comparable user spend'),
    colSupplierCost: e('渠道實際成本', '채널 실제 원가', 'Channel actual cost'),
    sourcesTitle: e('按業務來源', '업무 소스별', 'By source'),
    sourcesCopy: e('主對話、工具呼叫等業務來源的成本與免費補貼', '주요 대화, 도구 호출 등 소스별 원가와 무료 보조금', 'Cost and free subsidy by source such as main chat and tools'),
    sourcesCopyRate: e('來源歸因覆蓋率 {p} · 點選表頭排序', '소스 귀속 커버리지 {p} · 헤더를 눌러 정렬', 'Source attribution coverage {p} · click headers to sort'),
    colSource: e('來源', '소스', 'Source'),
    src: {
      main: e('主對話', '주요 대화', 'Main chat'),
      chat: e('對話', '대화', 'Chat'),
      tool: e('工具呼叫', '도구 호출', 'Tool calls'),
      image: e('生圖', '이미지 생성', 'Image generation'),
      unattributed: e('日誌已過期 / 未歸因', '로그 만료 / 미귀속', 'Expired / unattributed'),
    },
    footNote: e(
      '僅記帳請求在被標為「不走帳」的渠道上成本記 0，毛利只比較扣費請求。成本未知的請求不補 0，也不估算。',
      '기록만 하는 요청은 「정산 안 함」 채널에서 원가를 0으로 기록하고, 총이익은 차감 요청만 비교해요. 원가를 모르는 요청은 0으로 채우지도 추정하지도 않아요.',
      'Metered-only requests on channels marked unbilled record cost 0, and margin compares charged requests only. Unknown-cost requests are neither zero-filled nor estimated.',
    ),
  },

  // ------------------------------------------------------------------ 付费分析
  an: {
    desc: e('復購率、首購隊列、付費帳號畫像與模型偏好的深度經營視圖', '재구매율, 첫 구매 코호트, 결제 계정 프로필, 모델 선호를 깊이 보는 경영 뷰', 'Repurchase, first-purchase cohorts, payer profile and model preference'),
    cached: e('快取結果 · {t}', '캐시된 결과 · {t}', 'Cached · {t}'),
    recomputed: e('剛剛計算 · {t}', '방금 계산 · {t}', 'Just computed · {t}'),
    recompute: e('重新計算', '다시 계산', 'Recompute'),
    computing: e('計算中…', '계산 중…', 'Computing…'),
    note: e(
      '口徑：在線支付實收（已支付並扣除退款），剔除僅記帳請求；用量取計費成功記錄，生圖按模型類型判定；時間按北京時間。結果快取 6 小時，需要即時資料點右上角「重新計算」。',
      '기준: 온라인 결제 실수령(결제 완료, 환불 차감), 기록만 하는 요청 제외. 사용량은 과금 성공 기록이고 이미지 생성은 모델 유형으로 판정해요. 시간은 베이징 시간이에요. 결과는 6시간 캐시되며 즉시 데이터가 필요하면 오른쪽 위 「다시 계산」을 누르세요.',
      'Basis: online payments received (paid, net of refunds), metered-only requests excluded. Usage is successful billed records, image generation is detected by model type, time is Beijing time. Results are cached for 6 hours; use Recompute for live data.',
    ),
    kPayers: e('付費帳號', '결제 계정', 'Paying accounts'),
    kOrders: e('{n} 筆訂單 · 實收 {v}', '주문 {n}건 · 실수령 {v}', '{n} orders · received {v}'),
    kRate: e('復購率', '재구매율', 'Repurchase rate'),
    kRepurchasers: e('{n} 個帳號復購', '{n}곳 재구매', '{n} repurchased'),
    kRevShare: e('復購帳號收入佔比', '재구매 계정 매출 비중', 'Repurchaser revenue share'),
    kRev: e('實收 {v}', '실수령 {v}', 'Received {v}'),
    kInterval: e('首購 → 二購間隔', '첫 구매 → 두 번째 구매 간격', 'First → second purchase gap'),
    days: e('{n} 天', '{n}일', '{n} d'),
    median: e('中位 {n} 天', '중앙값 {n}일', 'Median {n} d'),
    noSample: e('暫無復購樣本', '재구매 표본이 없어요', 'No repurchase sample'),
    kM21: e('成熟復購率 · 首購滿 21 天', '성숙 재구매율 · 첫 구매 후 21일', 'Mature repurchase · 21 days since first'),
    kM21Sub: e('{a} / {b} 個帳號', '{a} / {b}곳', '{a} / {b} accounts'),
    kM14: e('成熟復購率 · 首購滿 14 天', '성숙 재구매율 · 첫 구매 후 14일', 'Mature repurchase · 14 days since first'),
    kM14Sub: e('其中 14 天內復購 {p}', '그중 14일 안 재구매 {p}', 'Within 14 days {p}'),
    kTokens: e('付費帳號 Token 消耗', '결제 계정 토큰 사용량', 'Payer token usage'),
    kRequests: e('{n} 次請求 · {v}', '요청 {n}건 · {v}', '{n} requests · {v}'),
    kImages: e('付費帳號生圖', '결제 계정 이미지 생성', 'Payer image generation'),
    imagesUnit: e('{n} 張', '{n}장', '{n} images'),
    kUsageShare: e('佔付費與免費帳號總消耗 {p}', '결제/무료 계정 전체 소비의 {p}', '{p} of total payer and free spend'),
    cDist: e('購買次數分布', '구매 횟수 분포', 'Purchase count distribution'),
    cDistSub: e('橫軸：累計支付次數', '가로축: 누적 결제 횟수', 'x: cumulative payments'),
    times: e('{n} 次', '{n}회', '{n}x'),
    users: e('帳號數', '계정 수', 'Accounts'),
    cCohort: e('首購周隊列復購', '첫 구매 주간 코호트 재구매', 'Weekly first-purchase cohorts'),
    cCohortSub: e('近幾周隊列觀察期未走完，復購數偏低屬正常', '최근 몇 주 코호트는 관찰 기간이 끝나지 않아 재구매가 적은 것이 정상이에요', 'Recent cohorts are still maturing, low repurchase is normal'),
    weekLabel: e('{d} 起', '{d} 주', 'Week of {d}'),
    cohortUsers: e('首購帳號', '첫 구매 계정', 'First purchasers'),
    cohortRepurchased: e('已復購', '재구매함', 'Repurchased'),
    cMonthly: e('月度收入構成', '월별 매출 구성', 'Monthly revenue mix'),
    cMonthlySub: e('新客首月 / 老客復購（$）', '신규 첫 달 / 기존 재구매 ($)', 'new first month / returning ($)'),
    newRevenue: e('新客', '신규', 'New'),
    returningRevenue: e('老客復購', '기존 재구매', 'Returning'),
    cModels: e('付費帳號模型偏好 TOP 10', '결제 계정 모델 선호 TOP 10', 'Payer model preference TOP 10'),
    cModelsSub: e('按消耗金額（$）', '소비 금액 기준 ($)', 'by spend ($)'),
    spendUsd: e('消耗（$）', '소비 ($)', 'Spend ($)'),
    channelTitle: e('渠道拆分', '채널 분해', 'Channel split'),
    channelSub: e('「在哪扇門付的」與「誰帶來的」是兩個問題，分開看，不要相加', '「어디서 결제했나」와 「누가 데려왔나」는 서로 다른 질문이에요. 따로 보고 더하지 마세요', 'Where they paid and who brought them are separate questions; read them apart, do not add'),
    channelNote: e(
      '本倉只有控制台一個下單入口，註冊時也不記錄來源，沒有支付入口與獲客來源的資料，下面兩張表與堆疊圖顯示空狀態。對應 Alice 的「支付入口」「獲客來源」，已登記為沒有對應物，不用推測資料填充。',
      '이 저장소는 콘솔이 유일한 주문 진입로이고 가입 시 유입 경로도 기록하지 않아요. 결제 진입로와 획득 소스 데이터가 없어 아래 두 표와 누적 차트는 빈 상태로 보여요. Alice의 「결제 진입로」「획득 소스」에 해당하며 대응물 없음으로 등록했고 추정 데이터로 채우지 않아요.',
      'This repository has a single checkout entry (the console) and records no signup source, so there is no payment entry or acquisition data; the two tables and the stacked chart show empty states. They map to Alice payment entry and acquisition source and are registered as having no counterpart; no guessed data is used.',
    ),
    entryTitle: e('支付入口', '결제 진입로', 'Payment entry'),
    entrySub: e('這筆錢在哪扇門付的', '이 결제가 어디서 이뤄졌나', 'where the money was paid'),
    colEntry: e('入口', '진입로', 'Entry'),
    colNet: e('實收', '실수령', 'Received'),
    colShare: e('佔比', '비중', 'Share'),
    colOrders: e('訂單', '주문', 'Orders'),
    colPayers: e('付費帳號', '결제 계정', 'Paying accounts'),
    colSince: e('資料起始', '데이터 시작', 'Data since'),
    acqTitle: e('獲客來源', '획득 소스', 'Acquisition source'),
    acqSub: e('這個付費帳號是誰帶來的', '이 결제 계정을 누가 데려왔나', 'who brought this payer'),
    colSource: e('來源', '소스', 'Source'),
    colRate: e('復購率', '재구매율', 'Repurchase rate'),
    colAvgPay: e('人均儲值', '계정당 충전', 'Avg recharge'),
    noChannelData: e('暫無資料：本倉沒有對應的資料來源', '데이터 없음: 이 저장소에는 해당 데이터 소스가 없어요', 'No data: this repository has no matching source'),
    entryMonthly: e('各支付入口月度實收', '결제 진입로별 월 실수령', 'Monthly receipts by entry'),
    entryMonthlySub: e('$ · 堆疊', '$ · 누적', '$ · stacked'),
    segTitle: e('付費帳號分型', '결제 계정 유형', 'Payer segments'),
    segSub: e('按生圖消耗佔個人總消耗的比例分桶', '이미지 생성 소비가 전체 소비에서 차지하는 비율로 분류', 'bucketed by image share of total spend'),
    seg: {
      text_heavy: e('文字為主', '텍스트 중심', 'Text heavy'),
      image_heavy: e('生圖為主', '이미지 중심', 'Image heavy'),
      mixed: e('圖文混合', '혼합', 'Mixed'),
      no_usage: e('暫無消耗', '소비 없음', 'No usage'),
    },
    colSegment: e('分型', '유형', 'Segment'),
    colUsers: e('帳號數', '계정 수', 'Accounts'),
    colContribution: e('儲值貢獻', '충전 기여', 'Recharge'),
    colRepurchasers: e('復購帳號', '재구매 계정', 'Repurchasers'),
    colAvgSpend: e('帳號平均消耗', '계정당 소비', 'Avg spend'),
    cmpTitle: e('復購帳號 vs 單次購買帳號', '재구매 계정 vs 1회 구매 계정', 'Repurchasers vs single purchasers'),
    cmpSub: e('帳號平均口徑', '계정 평균 기준', 'per-account average'),
    cmpMetric: e('指標', '지표', 'Metric'),
    cmpSingle: e('單次購買', '1회 구매', 'Single'),
    cmpRepeat: e('復購帳號', '재구매 계정', 'Repurchasers'),
    cmpMultiple: e('倍數', '배수', 'Multiple'),
    cmpUsers: e('帳號數', '계정 수', 'Accounts'),
    cmpPay: e('平均儲值', '평균 충전', 'Avg recharge'),
    cmpCost: e('平均消耗', '평균 소비', 'Avg spend'),
    cmpTokens: e('平均 Token（百萬）', '평균 토큰 (백만)', 'Avg tokens (M)'),
    cmpImages: e('平均生圖', '평균 이미지 생성', 'Avg images'),
    cmpDays: e('平均活躍天數', '평균 활성 일수', 'Avg active days'),
    topTitle: e('儲值 TOP 20 帳號', '충전 TOP 20 계정', 'Top 20 payers'),
    topSub: e('儲值金額降序，含用量與主力模型', '충전 금액 내림차순, 사용량과 주력 모델 포함', 'by recharge, with usage and top model'),
    colOrg: e('帳號', '계정', 'Account'),
    colFirstPaid: e('首次支付', '첫 결제', 'First paid'),
    colTimes: e('次數', '횟수', 'Times'),
    colRecharge: e('儲值', '충전', 'Recharge'),
    colSpend: e('消耗', '소비', 'Spend'),
    colImages: e('生圖', '이미지', 'Images'),
    colActive: e('活躍天數', '활성 일수', 'Active days'),
    colTopModel: e('主力模型', '주력 모델', 'Top model'),
    repTitle: e('分析報告', '분석 리포트', 'Analysis reports'),
    repSub: e('所有管理員共享，可匯入外部工具生成的報告', '모든 관리자가 공유하며 외부 도구로 만든 리포트를 가져올 수 있어요', 'shared by all admins; import reports made with external tools'),
    repImport: e('匯入報告', '리포트 가져오기', 'Import report'),
    repNote: e(
      '本倉沒有後台分析 Agent，Alice 的「生成報告」「追問」沒有對應物，已登記。外部工具（Cursor 等）寫好的 Markdown 報告可以匯入這裡長期保存、所有管理員可見。',
      '이 저장소에는 백그라운드 분석 에이전트가 없어 Alice의 「리포트 생성」「추가 질문」에 해당하는 기능이 없고 등록해 두었어요. 외부 도구(Cursor 등)로 작성한 Markdown 리포트를 가져와 오래 보관하고 모든 관리자가 볼 수 있어요.',
      'This repository has no background analyst agent, so Alice report generation and follow-up have no counterpart (registered). Markdown reports written with external tools (Cursor etc.) can be imported and kept for all admins.',
    ),
    repEmpty: e('還沒有報告', '리포트가 없어요', 'No reports yet'),
    repNo: e('報告 #{n}', '리포트 #{n}', 'Report #{n}'),
    repAgent: e('AI 生成', 'AI 생성', 'AI'),
    repImport2: e('匯入', '가져옴', 'Imported'),
    repCollapse: e('收起', '접기', 'Collapse'),
    repImported: e('報告已匯入', '리포트를 가져왔어요', 'Report imported'),
    repImportBtn: e('匯入', '가져오기', 'Import'),
    repFieldTitle: e('標題', '제목', 'Title'),
    repFieldContent: e('正文（Markdown）', '본문 (Markdown)', 'Content (Markdown)'),
    repFieldHint: e('支援標題、列表、表格、粗體、行內程式碼、程式碼塊與引用，不執行任何腳本', '제목, 목록, 표, 굵게, 인라인 코드, 코드 블록, 인용을 지원하며 스크립트는 실행하지 않아요', 'Supports headings, lists, tables, bold, inline code, code blocks and quotes; scripts never run'),
  },
}

const h = (zhT: string, koT: string, enT: string, zhB: string, koB: string, enB: string) => ({ title: e(zhT, koT, enT), body: e(zhB, koB, enB) })

/** 指标说明：移植自 Alice 的 _caMetricHelp，字段名与口径按本仓改写（usage_records、upstream_cost、billing_mode）。 */
export const BIZ_HELP: Record<string, { title: Entry; body: Entry }> = {
  companyCost: h(
    '全站已知成本', '전체 확인 원가', 'Company known cost',
    '所选日期内，外部请求与仅记账请求所有成功账单中 upstream_cost 非空部分的合计。采购价未知的请求不计入，所以这是成本下限。',
    '선택 기간 동안 외부 요청과 기록만 하는 요청의 성공 과금 중 upstream_cost가 비어 있지 않은 부분의 합계예요. 매입가를 모르는 요청은 포함되지 않으므로 원가의 하한이에요.',
    'Sum of non-null upstream_cost across successful billing rows of external and metered-only requests in range. Requests with unknown purchase price are excluded, so this is a floor.',
  ),
  knownCost: h(
    '已知上游成本', '확인된 업스트림 원가', 'Known upstream cost',
    '只汇总成功账单中 upstream_cost 非空的金额。该字段来自上游返回的真实费用，或按实际渠道与模型采购价计算；NULL 会进入未知成本，不按 0 处理。',
    '성공 과금 중 upstream_cost가 비어 있지 않은 금액만 합산해요. 이 값은 업스트림이 돌려준 실제 비용이거나 실제 채널과 모델의 매입가로 계산한 값이며, NULL은 미확인 원가로 분류하고 0으로 처리하지 않아요.',
    'Sums upstream_cost only where it is non-null: the real cost returned by upstream, or computed from the actual channel and model purchase price. NULL goes to unknown cost and is never treated as 0.',
  ),
  internalCost: h(
    '仅记账已知成本', '기록만 하는 요청 확인 원가', 'Metered known cost',
    'billing_mode 为 metered 的请求在所选周期产生的已知上游成本，单独展示，不混入外部请求的经营毛利；采购价未知部分同样不计入金额。',
    'billing_mode가 metered인 요청이 선택 기간에 만든 확인된 업스트림 원가예요. 따로 보여주며 외부 요청의 경영 총이익에 섞지 않아요. 매입가를 모르는 부분도 금액에 넣지 않아요.',
    'Known upstream cost from requests with billing_mode metered in range. Shown separately, never mixed into external margin; unknown purchase prices are excluded too.',
  ),
  coverage: h(
    '成本覆盖率', '원가 커버리지', 'Cost coverage',
    'upstream_cost 非空的成功账单数 ÷ 全部成功账单数。衡量有多少请求能准确核算采购成本；低于 95% 时，已知成本只能视为下限。',
    'upstream_cost가 비어 있지 않은 성공 과금 수 ÷ 전체 성공 과금 수예요. 얼마나 많은 요청의 매입 원가를 정확히 계산할 수 있는지 보여주며, 95% 미만이면 확인 원가는 하한으로만 봐야 해요.',
    'Successful billing rows with non-null upstream_cost ÷ all successful rows. Measures how many requests have an accurate purchase cost; below 95% known cost is only a floor.',
  ),
  userCharge: h(
    '用户消耗面值', '사용자 소비 액면가', 'User spend (face value)',
    '外部请求成功账单的有效扣费合计（仅记账请求不扣费，不在其中）。它是所选周期内的花瓣消耗面值，不是现金收入；与「本期收款 / 累计收款」对照，才能看出这个账号是付钱在用还是在用赠送额度。',
    '외부 요청 성공 과금의 유효 차감 합계예요(기록만 하는 요청은 차감이 없어 포함되지 않아요). 선택 기간의 꽃잎 소비 액면가이며 현금 수입이 아니에요. 「이번 기간 수금 / 누적 수금」과 비교해야 결제해서 쓰는지 증정분으로 쓰는지 알 수 있어요.',
    'Effective charges on successful external requests (metered-only requests deduct nothing and are excluded). Face value of petals consumed, not cash; compare with paid-in-period and lifetime paid to tell paying usage from gifted usage.',
  ),
  periodPaid: h(
    '本期收款', '이번 기간 수금', 'Paid in period',
    '所选日期范围内该账号在线支付真实到账合计（按 paid_at 归日，已支付），已扣除退款。管理员调账、赠送、活动奖励都不计入。',
    '선택 기간에 해당 계정의 온라인 결제 실제 입금 합계예요(paid_at 기준 일자, 결제 완료). 환불은 차감하고, 관리자 조정, 증정, 이벤트 보상은 포함하지 않아요.',
    'Online payments actually received from the account in range (by paid_at, paid orders), net of refunds. Admin adjustments, gifts and campaign rewards are excluded.',
  ),
  lifetimePaid: h(
    '累计收款', '누적 수금', 'Lifetime paid',
    '该账号历史全部在线支付真实到账合计，已扣除退款，与上方日期筛选无关，看的是「这个账号到底给公司交过多少钱」。管理员调账、赠送、活动奖励都不计入。',
    '해당 계정의 전체 온라인 결제 실제 입금 합계이고 환불은 차감했어요. 위의 날짜 필터와 무관하며 「이 계정이 회사에 얼마를 냈는가」를 보여줘요. 관리자 조정, 증정, 이벤트 보상은 포함하지 않아요.',
    'All online payments ever received from the account, net of refunds, independent of the date filter: how much this account has really paid. Admin adjustments, gifts and campaign rewards are excluded.',
  ),
  margin: h(
    '已知样本毛利', '확인 샘플 총이익', 'Known-sample margin',
    '仅在 upstream_cost 已知的同一批账单内计算：有效用户扣费 − 已知上游成本。毛利率 = 该差额 ÷ 这批账单的有效用户扣费，不能外推为全站毛利。仅记账请求不扣费，不进入毛利。',
    'upstream_cost를 아는 같은 묶음의 과금에서만 계산해요: 유효 사용자 차감 − 확인된 업스트림 원가. 총이익률 = 이 차액 ÷ 이 묶음의 유효 사용자 차감이며 전체 총이익으로 확대 해석할 수 없어요. 기록만 하는 요청은 차감이 없어 총이익에 들어가지 않아요.',
    'Computed only within billing rows where upstream_cost is known: effective user charge − known upstream cost. Margin rate = that difference ÷ the effective charge of the same rows; do not extrapolate to the whole company. Metered-only requests carry no charge and are excluded.',
  ),
  freeCost: h(
    '免费功能已知成本', '무료 기능 확인 원가', 'Free-feature known cost',
    '有效用户扣费为 0、但 upstream_cost 已知的扣费类请求所产生的上游成本（工具调用等）。对用户免费，仍是公司的真实成本。',
    '유효 사용자 차감이 0이지만 upstream_cost를 아는 과금형 요청이 만든 업스트림 원가예요(도구 호출 등). 사용자에게는 무료여도 회사의 실제 원가예요.',
    'Upstream cost of charged-mode requests whose effective user charge is 0 but upstream_cost is known (tool calls and similar). Free to the user, still real cost to the company.',
  ),
  perUserCost: h(
    '账号平均已知成本', '계정당 확인 원가', 'Known cost per account',
    '外部请求已知上游成本 ÷ 所选周期内有成功账单的外部去重账号数。下方每千次请求成本 = 已知上游成本 ÷ 成功请求数 × 1000；覆盖不全时两者都是下限。',
    '외부 요청의 확인된 업스트림 원가 ÷ 선택 기간에 성공 과금이 있는 외부 고유 계정 수예요. 아래 요청 1,000건당 원가 = 확인 원가 ÷ 성공 요청 수 × 1000이며 커버리지가 부족하면 둘 다 하한이에요.',
    'Known upstream cost of external requests ÷ distinct external accounts with successful billing in range. Cost per 1,000 requests = known cost ÷ successful requests × 1000; both are floors when coverage is incomplete.',
  ),
  costChart: h(
    '免费业务补贴与每日毛利', '무료 업무 보조금과 일일 총이익', 'Free subsidy and daily margin',
    '按北京时间自然日展示综合已知毛利、用户对话已知毛利和免费业务补贴。补贴作为负项显示；业务归因低于 95% 的日期不绘制对话与免费业务拆分，但综合毛利仍正常展示。',
    '베이징 시간 일 단위로 종합 확인 총이익, 사용자 대화 확인 총이익, 무료 업무 보조금을 보여줘요. 보조금은 음수로 표시하고, 업무 귀속이 95% 미만인 날은 대화와 무료 업무 분해를 그리지 않지만 종합 총이익은 그대로 보여줘요.',
    'Per Beijing calendar day: overall known margin, conversation known margin and free-business subsidy. Subsidy is shown negative; days with attribution below 95% skip the conversation/free split but keep the overall margin.',
  ),
  coverageChart: h(
    '每日成本覆盖率', '일일 원가 커버리지', 'Daily cost coverage',
    '每天 upstream_cost 非空的外部成功账单数 ÷ 当天全部外部成功账单数。95% 虚线是可以直接用于经营判断的参考完整线。',
    '하루 동안 upstream_cost가 비어 있지 않은 외부 성공 과금 수 ÷ 그날 전체 외부 성공 과금 수예요. 95% 점선은 경영 판단에 바로 쓸 수 있는 기준선이에요.',
    'Per day: external successful rows with non-null upstream_cost ÷ all external successful rows. The 95% dashed line is the reference for decision-grade data.',
  ),
  date: h('日期', '날짜', 'Date', '以 Asia/Shanghai 北京时间划分自然日，起止日期均包含。', 'Asia/Shanghai 베이징 시간으로 일을 나누며 시작일과 종료일을 모두 포함해요.', 'Calendar days in Asia/Shanghai (Beijing time); both ends are inclusive.'),
  activeUsers: h(
    '活跃账号', '활성 계정', 'Active accounts',
    '所选分组内至少产生一条成功计费记录的去重外部账号数；失败请求不会进入。',
    '선택한 그룹에서 성공 과금 기록이 하나 이상 있는 외부 고유 계정 수예요. 실패한 요청은 포함되지 않아요.',
    'Distinct external accounts with at least one successful billing row in the group; failed requests are not counted.',
  ),
  requests: h(
    '请求', '요청', 'Requests',
    'usage_records 中 status 为 ok 的行数。失败请求不进入用量记录，因此不包含在这里。',
    'usage_records에서 status가 ok인 행 수예요. 실패한 요청은 사용량 기록에 들어가지 않아 포함되지 않아요.',
    'Rows in usage_records with status ok. Failed requests do not write usage rows and are not included.',
  ),
  unknownRequests: h(
    '未知请求', '미확인 요청', 'Unknown requests',
    '成功账单中 upstream_cost 为 NULL 的数量。NULL 表示实际渠道与模型缺少可核算采购价，不表示上游成本为 0。',
    '성공 과금 중 upstream_cost가 NULL인 건수예요. NULL은 실제 채널과 모델에 계산 가능한 매입가가 없다는 뜻이며 업스트림 원가가 0이라는 뜻이 아니에요.',
    'Successful rows where upstream_cost is NULL. NULL means the actual channel and model lack a computable purchase price, not zero upstream cost.',
  ),
  unknownTokens: h(
    '未知 Token', '미확인 토큰', 'Unknown tokens',
    '所有成本未知请求的输入 Token + 输出 Token。缓存命中属于输入 Token 的组成，不在这里重复相加。用于衡量未知成本敞口的规模。',
    '원가를 모르는 모든 요청의 입력 토큰 + 출력 토큰이에요. 캐시 적중은 입력 토큰에 포함되어 있어 중복 합산하지 않아요. 미확인 원가 노출 규모를 가늠하는 데 써요.',
    'Input + output tokens of all unknown-cost requests. Cache hits are already part of input tokens and are not added twice. Indicates the size of unknown-cost exposure.',
  ),
  user: h(
    '账号', '계정', 'Account',
    '外部真实账号：已排除只有仅记账请求的账号。点击账号名可进入账号详情。',
    '외부 실제 계정이에요. 기록만 하는 요청만 있는 계정은 제외해요. 계정 이름을 누르면 계정 상세로 이동해요.',
    'Real external accounts; accounts with only metered-only requests are excluded. Click a name to open the account detail.',
  ),
  activeDays: h(
    '活跃天数', '활성 일수', 'Active days',
    '该账号在所选日期范围内产生成功账单的北京时间去重自然日数。',
    '해당 계정이 선택 기간에 성공 과금을 만든 베이징 시간 기준 고유 일수예요.',
    'Distinct Beijing calendar days in range on which the account had successful billing.',
  ),
  tokens: h(
    'Token', '토큰', 'Tokens',
    '该账号成功账单的输入 Token + 输出 Token；缓存命中已经包含在输入统计里，不重复加总。',
    '해당 계정의 성공 과금 입력 토큰 + 출력 토큰이에요. 캐시 적중은 입력 통계에 이미 포함되어 중복 합산하지 않아요.',
    'Input + output tokens on the account successful billing; cache hits are already inside input and not added twice.',
  ),
  unknownCharge: h(
    '对应用户消耗', '해당 사용자 소비', 'Matching user spend',
    '成本未知账单对应的有效用户扣费面值。它只能表示用户侧消耗，不能拿来替代或估算上游采购成本。',
    '원가를 모르는 과금에 해당하는 유효 사용자 차감 액면가예요. 사용자 측 소비일 뿐이며 업스트림 매입 원가를 대체하거나 추정하는 데 쓸 수 없어요.',
    'Effective user charge on unknown-cost rows. It describes user-side spend only and cannot substitute for or estimate upstream purchase cost.',
  ),
  model: h(
    '模型', '모델', 'Model',
    '按账单最终实际路由的渠道与模型分组，不按用户请求时看到的模型壳名分组。',
    '과금이 최종적으로 실제 라우팅된 채널과 모델로 묶어요. 사용자가 요청할 때 본 모델 셸 이름으로 묶지 않아요.',
    'Grouped by the channel and model the billing finally routed to, not by the model shell name the caller saw.',
  ),
  provider: h(
    '实际渠道', '실제 채널', 'Actual channel',
    '账单最终执行请求的渠道。同一个模型如果路由到不同渠道，会拆成不同的统计行，避免采购价格和毛利被平均混在一起。',
    '과금이 최종적으로 요청을 처리한 채널이에요. 같은 모델이 다른 채널로 라우팅되면 별도 행으로 나뉘어 매입가와 총이익이 평균으로 섞이지 않아요.',
    'The channel that finally served the request. The same model routed to different channels becomes separate rows so purchase price and margin are not averaged together.',
  ),
  modelUserCharge: h(
    '用户总消耗', '사용자 총 소비', 'Total user spend',
    '该模型与渠道组合全部成功账单的有效花瓣扣费合计，含渠道成本已知和未知的请求。是用户消费面值，不是所选周期的现金充值收入。',
    '해당 모델과 채널 조합의 모든 성공 과금의 유효 꽃잎 차감 합계예요. 채널 원가를 아는 요청과 모르는 요청을 모두 포함해요. 사용자 소비 액면가이며 선택 기간의 현금 충전 수입이 아니에요.',
    'Effective petal charges across all successful rows of the model and channel pair, known and unknown cost alike. User face value, not cash recharge in the period.',
  ),
  modelComparableCharge: h(
    '可比用户消耗', '비교 가능 사용자 소비', 'Comparable user spend',
    '只汇总 upstream_cost 已知请求对应的有效花瓣扣费，用来与同一批渠道成本计算毛利；不拿成本未知请求的收入去虚增已知毛利。',
    'upstream_cost를 아는 요청에 해당하는 유효 꽃잎 차감만 합산해 같은 묶음의 채널 원가로 총이익을 계산해요. 원가를 모르는 요청의 매출로 확인 총이익을 부풀리지 않아요.',
    'Sums effective petal charges only for requests with known upstream_cost, to compute margin against the same rows; revenue from unknown-cost requests never inflates known margin.',
  ),
  supplierCost: h(
    '渠道实际成本', '채널 실제 원가', 'Channel actual cost',
    '该模型与渠道组合中 upstream_cost 非空金额的合计。来源是上游返回费用或按实际采购价计算的成本记录；NULL 单列为未知，不按 0 处理，也不等同于渠道方的月度账单。',
    '해당 모델과 채널 조합에서 upstream_cost가 비어 있지 않은 금액의 합계예요. 업스트림이 돌려준 비용이나 실제 매입가로 계산한 기록이며, NULL은 미확인으로 따로 두고 0으로 처리하지 않아요. 채널 사업자의 월 청구서와 같지 않아요.',
    'Sum of non-null upstream_cost for the model and channel pair, from upstream-returned cost or actual purchase price; NULL is listed as unknown, not 0, and is not the vendor monthly invoice.',
  ),
  source: h(
    '业务来源', '업무 소스', 'Source',
    '按 usage_records.source 归因到主对话、工具调用等场景；来源为空的请求按 main 处理，不会被丢弃。',
    'usage_records.source로 주요 대화, 도구 호출 등 장면에 귀속해요. 소스가 비어 있는 요청은 main으로 처리하며 버리지 않아요.',
    'Attributed by usage_records.source to main chat, tool calls and so on; an empty source is treated as main and never dropped.',
  ),
  todayResult: h(
    '今日经营结果', '오늘 경영 결과', "Today's result",
    '固定统计北京时间今天 00:00 至当前时刻，不受下方日期筛选影响。综合毛利包含全部外部请求，并已把扣费为 0 的非对话请求的上游成本作为补贴扣除；仅记账请求的成本不计入。',
    '베이징 시간 오늘 00:00부터 현재까지를 고정 집계하며 아래 날짜 필터의 영향을 받지 않아요. 종합 총이익은 모든 외부 요청을 포함하고 차감이 0인 비대화 요청의 업스트림 원가를 보조금으로 뺐어요. 기록만 하는 요청의 원가는 넣지 않아요.',
    'Fixed to Beijing time today 00:00 until now, unaffected by the filter below. Overall margin covers all external requests and deducts as subsidy the upstream cost of non-conversation requests charged 0; metered-only cost is excluded.',
  ),
  todayOverallMargin: h(
    '今日综合已知毛利', '오늘 종합 확인 총이익', 'Overall known margin today',
    '今天全部外部请求中成本已知的有效花瓣扣费 − 已知上游成本。扣费为 0 的免费业务成本照常扣除；存在未知成本时只能说明「已知口径」是否盈利。',
    '오늘 모든 외부 요청 중 원가를 아는 유효 꽃잎 차감 − 확인된 업스트림 원가예요. 차감이 0인 무료 업무 원가는 그대로 빼요. 원가 미확인이 있으면 「확인된 기준」의 이익 여부만 말해줘요.',
    "Today's effective petal charges with known cost minus known upstream cost across external requests. Free-business cost with charge 0 is deducted; with unknown cost this only tells whether the known basis is profitable.",
  ),
  todayConversationRate: h(
    '用户对话毛利率', '사용자 대화 총이익률', 'Conversation margin rate',
    '今天对话来源（main、chat、来源为空）中，仅对成本已知的同批请求计算：（有效扣费 − 已知上游成本）÷ 有效扣费。反映对话消费业务的单位经济性，不等于今日在线支付到账利润率。',
    '오늘 대화 소스(main, chat, 소스 비어 있음) 중 원가를 아는 같은 묶음의 요청만으로 계산해요: (유효 차감 − 확인 업스트림 원가) ÷ 유효 차감. 대화 소비 업무의 단위 경제성을 보여주며 오늘 온라인 결제 입금 이익률과 같지 않아요.',
    'Over conversation sources (main, chat, empty) today, on the same known-cost rows only: (effective charge − known upstream cost) ÷ effective charge. Unit economics of conversation spend, not the margin on payments received today.',
  ),
  todayConversationMargin: h(
    '用户对话已知毛利', '사용자 대화 확인 총이익', 'Conversation known margin',
    '今天对话来源的成本已知请求中，有效花瓣扣费减去已知上游成本。免费业务补贴不会混入这个数。',
    '오늘 대화 소스에서 원가를 아는 요청의 유효 꽃잎 차감에서 확인 업스트림 원가를 뺀 값이에요. 무료 업무 보조금은 이 값에 섞이지 않아요.',
    'Effective petal charges minus known upstream cost over known-cost conversation requests today. Free-business subsidy is not mixed in.',
  ),
  todayMomentsSubsidy: h(
    '免费业务补贴', '무료 업무 보조금', 'Free-business subsidy',
    '今天非对话来源中，用户有效扣费为 0 的请求所产生的已知上游成本，例如工具调用。这是公司承担的补贴，不是用户收入；收费的非对话请求归入其他业务。',
    '오늘 비대화 소스 중 사용자 유효 차감이 0인 요청이 만든 확인 업스트림 원가예요(예: 도구 호출). 회사가 부담하는 보조금이며 사용자 수입이 아니에요. 과금되는 비대화 요청은 기타 업무로 분류해요.',
    'Known upstream cost of non-conversation requests today with user charge 0, such as tool calls. This is subsidy borne by the company, not user revenue; charged non-conversation requests go to Other.',
  ),
  todayBusiness: h(
    '今日业务拆分', '오늘 업무 분해', 'Business split today',
    '用户对话只含 main、chat 与来源为空；免费业务补贴只含非对话来源且扣费为 0 的请求；收费的非对话请求归入其他业务；无法归因的请求单列。综合行是全部外部请求。',
    '사용자 대화는 main, chat, 소스 비어 있음만 포함해요. 무료 업무 보조금은 비대화 소스이면서 차감이 0인 요청만 포함하고, 과금되는 비대화 요청은 기타 업무로 가요. 귀속할 수 없는 요청은 따로 보여줘요. 종합 행은 모든 외부 요청이에요.',
    'Conversation covers main, chat and empty sources; free-business subsidy covers non-conversation requests charged 0; charged non-conversation requests are Other; unattributable requests are listed apart. The overall row is all external requests.',
  ),
  dailyOverallMargin: h(
    '每日综合已知毛利', '일별 종합 확인 총이익', 'Daily overall margin',
    '每天全部外部请求中成本已知的有效花瓣扣费 − 已知上游成本，已扣除当天能识别出的免费业务补贴。综合毛利率只使用成本已知的同批请求。',
    '하루 동안 모든 외부 요청 중 원가를 아는 유효 꽃잎 차감 − 확인 업스트림 원가이며 그날 식별되는 무료 업무 보조금은 이미 뺐어요. 종합 총이익률은 원가를 아는 같은 묶음의 요청만 써요.',
    'Per day: known-cost effective charges minus known upstream cost across external requests, after the free-business subsidy identified that day. The rate uses the same known-cost rows only.',
  ),
  dailyConversationMargin: h(
    '每日用户对话毛利', '일별 사용자 대화 총이익', 'Daily conversation margin',
    '每天对话来源中成本已知请求的有效花瓣扣费 − 已知上游成本，并计算同批请求毛利率。',
    '하루 동안 대화 소스에서 원가를 아는 요청의 유효 꽃잎 차감 − 확인 업스트림 원가이며 같은 묶음의 총이익률도 계산해요.',
    'Per day: effective charges minus known upstream cost over known-cost conversation requests, with the margin rate of the same rows.',
  ),
  dailyMomentsSubsidy: h(
    '每日免费业务补贴', '일별 무료 업무 보조금', 'Daily free-business subsidy',
    '每天非对话来源且用户有效扣费为 0 的请求所产生的已知上游成本。业务归因率低于 95% 时图表不展示拆分。',
    '하루 동안 비대화 소스이면서 사용자 유효 차감이 0인 요청이 만든 확인 업스트림 원가예요. 업무 귀속률이 95% 미만이면 차트에 분해를 보여주지 않아요.',
    'Per day: known upstream cost of non-conversation requests with user charge 0. Chart splits are hidden when attribution is below 95%.',
  ),
  businessAttribution: h(
    '业务归因率', '업무 귀속률', 'Attribution rate',
    '当天能归到具体来源的外部成功账单数 ÷ 当天全部外部成功账单数。低于 95% 时，对话与免费业务拆分隐藏；综合毛利仍可按账单计算。',
    '그날 구체적인 소스로 귀속되는 외부 성공 과금 수 ÷ 그날 전체 외부 성공 과금 수예요. 95% 미만이면 대화와 무료 업무 분해를 숨기고 종합 총이익은 과금 기준으로 계속 계산해요.',
    'External successful rows attributable to a source ÷ all external successful rows that day. Below 95% the conversation/free split is hidden; overall margin is still computed from billing rows.',
  ),
  bcDecision: h(
    '充值活动建议', '충전 이벤트 제안', 'Campaign recommendation',
    '系统同时检查五个信号：付费活跃账号余额压力、近 7 天真实收款环比、花瓣消费动能、活跃账号总库存覆盖天数，以及是否已有特殊活动（本页暂未读取活动）。建议分为暂缓、定向、全量和观察，不会自动修改活动。',
    '다섯 가지 신호를 함께 봐요: 결제 활성 계정의 잔액 압박, 최근 7일 실제 수금 전기 대비, 꽃잎 소비 모멘텀, 활성 계정 총 재고 커버 일수, 특별 이벤트 여부(이 페이지는 아직 이벤트를 읽지 않아요). 제안은 보류, 타깃, 전체, 관찰로 나뉘며 이벤트를 자동으로 바꾸지 않아요.',
    'Checks five signals at once: payer balance pressure, 7-day real payments trend, petal spend momentum, total active stock runway, and whether a special campaign exists (not read on this page yet). Outcomes are wait, targeted, broad or observe; campaigns are never changed automatically.',
  ),
  bcActive: h(
    '近 7 天活跃账号', '최근 7일 활성 계정', 'Active accounts (7d)',
    '最近 7×24 小时内至少产生一条成功扣费记录的外部账号。仅记账请求不算活跃。',
    '최근 7×24시간 안에 성공 차감 기록이 하나 이상 있는 외부 계정이에요. 기록만 하는 요청은 활성으로 보지 않아요.',
    'External accounts with at least one successful charged record in the last 7×24 hours. Metered-only requests do not count as active.',
  ),
  bcBalance: h(
    '活跃账号存量', '활성 계정 재고', 'Active account stock',
    '近 7 天活跃账号当前余额（付费钱包与赠送余额之和，负余额按 0 处理）的合计，按 1 美元 = 1000 花瓣换算。只统计仍在使用产品的账号。',
    '최근 7일 활성 계정의 현재 잔액(유상 지갑 + 증정 잔액, 음수는 0 처리) 합계이며 1달러 = 꽃잎 1000으로 환산해요. 제품을 계속 쓰는 계정만 집계해요.',
    'Sum of current balances (paid wallet plus gifted balance, negatives as 0) of accounts active in 7 days, at 1 USD = 1000 petals. Only accounts still using the product are counted.',
  ),
  bcRunway: h(
    '存量覆盖天数', '재고 커버 일수', 'Stock runway',
    '活跃账号当前可用余额 ÷ 近 7 天日均花瓣消耗。天数越高说明库存越足，越不适合继续普遍赠送。',
    '활성 계정의 현재 사용 가능 잔액 ÷ 최근 7일 일평균 꽃잎 소비예요. 일수가 클수록 재고가 넉넉해 전반적인 증정을 계속하기에 맞지 않아요.',
    'Current usable balance of active accounts ÷ 7-day daily petal spend. More days means more stock and less reason to keep gifting broadly.',
  ),
  bcUrgent: h(
    '立即充值账号', '즉시 충전 계정', 'Recharge-now accounts',
    '近 7 天仍活跃、有在线支付历史、近 7 天产生花瓣消耗，且按当前速度余额预计 7 天内耗尽的账号。',
    '최근 7일에도 활성이고 온라인 결제 이력이 있으며 최근 7일 꽃잎 소비가 있고 현재 속도로 잔액이 7일 안에 바닥날 계정이에요.',
    'Accounts active in 7 days with online payment history and petal spend, whose balance runs out within 7 days at the current pace.',
  ),
  bcSpend: h(
    '近 7 天消耗', '최근 7일 소비', '7-day spend',
    '成功账单的用户侧扣费合计，按 1 美元 = 1000 花瓣换算；仅记账与免费请求扣费为 0。',
    '성공 과금의 사용자 측 차감 합계이며 1달러 = 꽃잎 1000으로 환산해요. 기록만 하는 요청과 무료 요청은 차감이 0이에요.',
    'Sum of user-side charges on successful billing at 1 USD = 1000 petals; metered-only and free requests charge 0.',
  ),
  bcTrend: h(
    '收款与消耗趋势', '수금과 소비 추이', 'Payments and spend trend',
    '真实收款只统计在线支付成功并扣除退款的订单，按 paid_at 北京时间归日；花瓣消耗是外部请求成功账单的有效扣费，不等于现金收入。',
    '실제 수금은 온라인 결제에 성공하고 환불을 뺀 주문만 집계하며 paid_at 베이징 시간 기준으로 일자를 나눠요. 꽃잎 소비는 외부 요청 성공 과금의 유효 차감이며 현금 수입과 같지 않아요.',
    'Real payments count only successful online orders net of refunds, by paid_at in Beijing time; petal spend is effective charges on successful external billing, not cash income.',
  ),
  bcActiveTrend: h(
    '活跃账号趋势', '활성 계정 추이', 'Active account trend',
    '每天至少产生一条成功扣费记录的外部去重账号数；付费账号是当天有扣费且有付费历史的账号。',
    '하루에 성공 차감 기록이 하나 이상 있는 외부 고유 계정 수예요. 결제 계정은 그날 차감이 있고 결제 이력이 있는 계정이에요.',
    'Distinct external accounts with at least one successful charged record each day; paying accounts are those with a charge that day and payment history.',
  ),
  bcSegment: h(
    '经营分层', '경영 세그먼트', 'Segments',
    '先按近 7 天是否产生花瓣消耗、是否有在线支付历史，再按余额可用天数分层。分层每次打开自动重算，同一账号只进入一个层级。',
    '최근 7일 꽃잎 소비 여부와 온라인 결제 이력 여부로 먼저 나누고 잔액 사용 가능 일수로 다시 나눠요. 열 때마다 자동으로 다시 계산하며 한 계정은 한 세그먼트에만 들어가요.',
    'Split first by petal spend in 7 days and payment history, then by balance runway. Recomputed on every open; an account falls into exactly one segment.',
  ),
  bcMomentum: h(
    '消费动能', '소비 모멘텀', 'Spend momentum',
    '根据近 7 天活跃天数、花瓣消耗及相对前 7 天的变化生成高、中、低行为代理。用于排序运营优先级，不代表账号表达过购买意愿。',
    '최근 7일 활성 일수, 꽃잎 소비, 직전 7일 대비 변화로 높음, 보통, 낮음 행동 지표를 만들어요. 운영 우선순위를 정하는 용도이며 계정이 구매 의사를 밝혔다는 뜻이 아니에요.',
    'High, medium or low behavioral proxy from 7-day active days, petal spend and change vs the previous 7 days. Used to rank priorities, not a stated intent to buy.',
  ),
  bcInventory: h(
    '活跃账号存量结构', '활성 계정 재고 구조', 'Active stock structure',
    '只统计近 7 天活跃的外部账号。可用天数 = 当前可用余额 ÷ 近 7 天日均花瓣消耗；没有消耗的账号单独一列。已付费只认在线支付成功且未退款。',
    '최근 7일 활성인 외부 계정만 집계해요. 사용 가능 일수 = 현재 사용 가능 잔액 ÷ 최근 7일 일평균 꽃잎 소비이고 소비가 없는 계정은 따로 한 줄로 보여줘요. 결제함은 온라인 결제 성공이며 환불되지 않은 경우만 인정해요.',
    'Only external accounts active in 7 days. Runway = usable balance ÷ 7-day daily petal spend; accounts with no spend get their own row. Paid counts only successful, unrefunded online payments.',
  ),
}
